import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const API = process.env.REACT_APP_API_URL || "https://skyvigdb-backend.onrender.com/api";

const IME_TERMS = [
  "Anaphylaxis",
  "Anaphylactic reaction",
  "Stevens-Johnson syndrome",
  "Toxic epidermal necrolysis",
  "Agranulocytosis",
  "Seizure",
  "Drug reaction with eosinophilia and systemic symptoms",
  "Liver failure",
  "Hepatic failure",
  "Acute liver failure",
  "Acute hepatic failure",
  "Aplastic anaemia",
  "Acute kidney injury",
  "Pulmonary embolism",
  "Ventricular fibrillation",
  "Sudden death",
  "QT prolongation",
  "Electrocardiogram QT prolonged",
  "Angioedema",
  "Cardiac arrest",
  "Myocardial infarction",
  "Acute myocardial infarction",
  "Cerebrovascular accident",
  "Cerebral infarction",
  "Ischaemic stroke",
  "Sepsis",
  "Septic shock",
  "Shock",
  "Respiratory failure",
  "Acute respiratory failure",
  "Acute respiratory distress syndrome",
  "Pneumonia",
  "Interstitial lung disease",
  "Pulmonary fibrosis",
  "Pulmonary hypertension",
  "Cardiac failure",
  "Cardiogenic shock",
  "Torsade de pointes",
  "Ventricular tachycardia",
  "Ventricular arrhythmia",
  "Cardiac tamponade",
  "Aortic dissection",
  "Pancreatitis acute",
  "Hepatitis fulminant",
  "Hepatic necrosis",
  "Liver injury",
  "Drug-induced liver injury",
  "Renal failure",
  "Chronic kidney disease",
  "Rhabdomyolysis",
  "Thrombotic thrombocytopenic purpura",
  "Disseminated intravascular coagulation",
  "Haemolytic anaemia",
  "Neutropenia",
  "Febrile neutropenia",
  "Thrombocytopenia",
  "Pancytopenia",
  "Leukopenia",
  "Guillain-Barre syndrome",
  "Encephalitis",
  "Meningitis",
  "Coma",
  "Loss of consciousness",
  "Status epilepticus",
  "Optic neuritis",
  "Blindness",
  "Anaphylactic shock",
  "Anaphylactoid reaction",
  "Anaphylactoid shock",
  "Laryngeal oedema",
  "Acute pulmonary oedema",
  "Pulmonary haemorrhage",
  "Intestinal perforation",
  "Gastrointestinal haemorrhage",
  "Peritonitis",
  "Intestinal ischaemia",
  "Hepatic encephalopathy",
  "Erythema multiforme",
  "Acute generalised exanthematous pustulosis",
  "Leukaemia",
  "Lymphoma",
  "Tumour lysis syndrome",
  "Neuroleptic malignant syndrome",
  "Serotonin syndrome",
  "Diabetic ketoacidosis",
  "Hypoglycaemic coma",
  "Hyperkalaemia",
  "Hypokalaemia",
  "Acute coronary syndrome",
  "Acute left ventricular failure",
  "Cardiac failure acute",
  "Cardiac failure congestive",
  "Acute on chronic liver failure",
  "Bone marrow failure",
  "Cardiopulmonary failure",
  "Heart failure with reduced ejection fraction",
  "Cerebral haemorrhage",
  "Subarachnoid haemorrhage",
  "Cerebral venous thrombosis",
  "Coronary artery thrombosis",
  "Deep vein thrombosis",
  "Fat embolism syndrome",
  "Thrombosis with thrombocytopenia syndrome",
  "Atypical haemolytic uraemic syndrome",
  "Haemolytic uraemic syndrome",
  "Evans syndrome",
  "Antiphospholipid syndrome",
  "Severe cutaneous adverse reaction",
  "Toxic shock syndrome",
  "Endotoxic shock",
  "Hypovolaemic shock",
  "Distributive shock",
  "Obstructive shock",
  "Shock haemorrhagic",
  "Myocarditis",
  "Sudden cardiac death",
  "Completed suicide",
  "Suicide attempt"
];

const NULL_FLAVORS = [
  { code: "UNK",  label: "UNK — Unknown" },
  { code: "ASKU", label: "ASKU — Asked but unknown" },
  { code: "NASK", label: "NASK — Not asked" },
  { code: "MSK",  label: "MSK — Masked (Confidential)" },
];

const STAGES = [
  { name: "Triage",      step: 1 },
  { name: "Data Entry",  step: 2 },
  { name: "Medical",     step: 3 },
  { name: "Quality",     step: 4 },
  { name: "Submissions", step: 5 },
  { name: "Archived",    step: 6 },
];

const AGENCIES = [
  { id: "fda",    label: "FDA",           country: "United States",  portal: "https://www.accessdata.fda.gov/scripts/foi/SpringFOI/FOIFullTextSearchAction.do" },
  { id: "ema",    label: "EMA",           country: "European Union", portal: "https://eudravigilance.ema.europa.eu" },
  { id: "mhra",   label: "MHRA",          country: "United Kingdom", portal: "https://yellowcard.mhra.gov.uk" },
  { id: "tga",    label: "TGA",           country: "Australia",      portal: "https://www.tga.gov.au/reporting-adverse-events" },
  { id: "hc",     label: "Health Canada", country: "Canada",         portal: "https://www.canada.ca/en/health-canada/services/drugs-health-products/medeffect-canada.html" },
  { id: "dcgi",   label: "DCGI",          country: "India",          portal: "https://cdsco.gov.in/opencms/opencms/en/pharmacovigilance/" },
  { id: "pmda",   label: "PMDA",          country: "Japan",          portal: "https://www.pmda.go.jp/english" },
  { id: "anvisa", label: "ANVISA",        country: "Brazil",         portal: "https://www.gov.br/anvisa" },
  { id: "sfda",   label: "SFDA",          country: "Saudi Arabia",   portal: "https://www.sfda.gov.sa" },
  { id: "other",  label: "Other",         country: "",               portal: "" },
];

const COUNTRIES = [
  "Argentina", "Australia", "Austria", "Belgium", "Brazil", "Canada", "Chile", "China",
  "Colombia", "Czech Republic", "Denmark", "Egypt", "Finland", "France", "Germany",
  "Greece", "Hungary", "India", "Indonesia", "Ireland", "Israel", "Italy", "Japan",
  "Malaysia", "Mexico", "Netherlands", "New Zealand", "Norway", "Peru", "Philippines",
  "Poland", "Portugal", "Romania", "Russia", "Saudi Arabia", "Singapore", "South Africa",
  "South Korea", "Spain", "Sweden", "Switzerland", "Taiwan", "Thailand", "Turkey",
  "United Arab Emirates", "United Kingdom", "United States", "Vietnam"
];

/* ================= HELPER & COMPLIANCE FUNCTIONS ================= */
const getColorClasses = (color) => {
  const maps = {
    indigo: { text: "text-indigo-700", border: "border-indigo-100", accent: "accent-indigo-600" },
    blue:   { text: "text-blue-700",   border: "border-blue-100",   accent: "accent-blue-600" },
    purple: { text: "text-purple-700", border: "border-purple-100", accent: "accent-purple-600" },
    yellow: { text: "text-yellow-700", border: "border-yellow-100", accent: "accent-yellow-600" },
    orange: { text: "text-orange-700", border: "border-orange-100", accent: "accent-orange-600" },
    red:    { text: "text-red-700",    border: "border-red-100",    accent: "accent-red-600" },
    green:  { text: "text-green-700",  border: "border-green-100",  accent: "accent-green-600" },
    amber:  { text: "text-amber-700",  border: "border-amber-100",  accent: "accent-amber-600" },
    slate:  { text: "text-slate-700",  border: "border-slate-100",  accent: "accent-slate-600" },
    violet: { text: "text-violet-700", border: "border-violet-100", accent: "accent-violet-600" },
  };
  return maps[color] || maps.indigo;
};

export const getUnifiedSeriousness = (caseObj) => {
  const gSer = caseObj?.general?.seriousness || {};
  const tSer = caseObj?.triage?.seriousness || {};
  const evList = caseObj?.events || [];
  const merged = {
    death: !!(gSer.death || tSer.death),
    lifeThreatening: !!(gSer.lifeThreatening || tSer.lifeThreatening),
    hospitalisation: !!(gSer.hospitalisation || gSer.hospitalised || tSer.hospitalisation),
    disability: !!(gSer.disability || tSer.disability),
    congenital: !!(gSer.congenital || tSer.congenital),
    medSignificant: !!(gSer.medSignificant || tSer.medSignificant),
  };
  evList.forEach(ev => {
    const es = ev?.seriousness || {};
    if (es.death || ev?.outcome === "Fatal") merged.death = true;
    if (es.lifeThreatening) merged.lifeThreatening = true;
    if (es.hospitalisation || es.hospitalised) merged.hospitalisation = true;
    if (es.disability) merged.disability = true;
    if (es.congenital) merged.congenital = true;
    if (es.medSignificant) merged.medSignificant = true;
    const pt = (ev?.pt || ev?.term || "").toLowerCase();
    if (IME_TERMS.some(t => t.toLowerCase() === pt)) merged.medSignificant = true;
  });
  const isSerious = Object.values(merged).some(Boolean);
  const isFatalOrLT = merged.death || merged.lifeThreatening;
  const fLtTag = merged.death ? "F" : merged.lifeThreatening ? "LT" : merged.hospitalisation ? "H" : "No";
  return { ...merged, isSerious, isFatalOrLT, fLtTag };
};

export const getDBWorkflowIndicator = (caseObj) => {
  if (!caseObj || caseObj.currentStep >= 6) {
    return { color: "slate", icon: "⚪", label: "Archived", daysTarget: null, daysLeft: null };
  }
  const ser = getUnifiedSeriousness(caseObj);
  const awareDate = caseObj.general?.awareDate || caseObj.triage?.receiptDate;
  if (!awareDate) {
    return { color: "slate", icon: "⚪", label: "No Aware Date", daysTarget: null, daysLeft: null };
  }
  const daysTarget = ser.isFatalOrLT ? 7 : ser.isSerious ? 15 : 90;
  const start = new Date(awareDate + "T00:00:00");
  const now = new Date();
  const elapsed = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  const daysLeft = daysTarget - elapsed;

  if (daysLeft < 0) {
    return { color: "red", icon: "🔴", label: `${daysLeft}d (${daysTarget}-day rule)`, daysTarget, daysLeft };
  }
  if (daysLeft <= 3) {
    return { color: "amber", icon: "🟡", label: `${daysLeft}d left (${daysTarget}d)`, daysTarget, daysLeft };
  }
  return { color: "green", icon: "🟢", label: `${daysLeft}d left (${daysTarget}d)`, daysTarget, daysLeft };
};

const calcDaysDiff = (d1, d2) => {
  if (!d1 || !d2) return "";
  const dt1 = new Date(d1);
  const dt2 = new Date(d2);
  if (isNaN(dt1) || isNaN(dt2)) return "";
  const diff = Math.round((dt2 - dt1) / (1000 * 60 * 60 * 24));
  return `${diff} day${Math.abs(diff) === 1 ? "" : "s"}`;
};

/* ================= SHARED UI PRIMITIVES ================= */
const F = (label, el, required = false, hint = "") => (
  <div className="flex flex-col gap-1 mb-3">
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
      {label}{required && <span className="text-red-500">*</span>}
    </label>
    {hint && <p className="text-xs text-slate-400 -mt-1">{hint}</p>}
    {el}
  </div>
);

const I = ({ nfValue, onNfChange, ...props }) => {
  const isNf = NULL_FLAVORS.some(n => n.code === props.value);
  return (
    <div className="flex items-center gap-1.5 w-full">
      <input
        className={`bg-white/50 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white w-full transition-all ${isNf ? "border-blue-400 bg-blue-50/40 font-mono font-bold text-blue-800" : "border-slate-200"}`}
        {...props}
      />
      {onNfChange && (
        <select
          title="DB Null Flavor (NF)"
          value={isNf ? props.value : ""}
          onChange={e => {
            if (e.target.value) {
              if (props.value && !isNf && !window.confirm("Existing field data will be replaced with Null Flavor. Continue?")) return;
              onNfChange(e.target.value);
            } else {
              onNfChange("");
            }
          }}
          className={`text-xs font-extrabold px-2 py-2 rounded-lg border cursor-pointer transition-colors ${isNf ? "bg-blue-600 text-white border-blue-600" : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"}`}
        >
          <option value="">NF</option>
          {NULL_FLAVORS.map(n => <option key={n.code} value={n.code}>{n.code}</option>)}
        </select>
      )}
    </div>
  );
};

const S = (opts, props) => (
  <select className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white w-full transition-all" {...props}>
    <option value="">— Select —</option>
    {opts.map(o => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);

const C = (label, checked, onChange, color = "indigo") => {
  const cls = getColorClasses(color);
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer py-1">
      <input type="checkbox" checked={!!checked} onChange={onChange} className={`mt-0.5 w-4 h-4 ${cls.accent} flex-shrink-0 cursor-pointer`} />
      <span className="text-slate-700 font-medium">{label}</span>
    </label>
  );
};

const TA = (props) => (
  <textarea className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white w-full resize-none transition-all" rows={3} {...props} />
);

const SectionHead = ({ children, color = "indigo" }) => {
  const cls = getColorClasses(color);
  return (
    <div className={`text-xs font-extrabold ${cls.text} uppercase tracking-widest border-b ${cls.border} pb-1 mb-4 mt-6`}>
      {children}
    </div>
  );
};

const MedDRAWidget = ({
  targetSection, targetIdx, currentPt, currentPtCode, currentLlt, currentSoc, currentVersion, isNonCurrent,
  meddraQuery, meddraResults, meddraLoading, meddraTarget, setMeddraTarget,
  searchMeddra, pickMeddra, onClear, setMeddraQuery, setMeddraResults,
  includeNonCurrent, setIncludeNonCurrent
}) => {
  const isActive = meddraTarget?.section === targetSection && meddraTarget?.idx === targetIdx;
  return (
    <div className="mt-2 mb-2">
      {currentPt && (
        <div className={`flex items-center gap-2 border rounded-xl px-3 py-2 text-xs mb-1 shadow-sm ${isNonCurrent ? "bg-red-50 border-red-300" : "bg-gradient-to-r from-purple-50 to-white border-purple-100"}`}>
          <span className={isNonCurrent ? "text-red-600 font-bold" : "text-green-600 font-bold"} title={isNonCurrent ? "Non-current term (*)" : "Term Coded"}>
            {isNonCurrent ? "⚠️ *" : "✔"}
          </span>
          <span className="text-purple-600 font-bold">MedDRA {currentVersion || "29.1"}:</span>
          <span className="font-semibold text-purple-900">{currentPt}</span>
          {currentPtCode && <span className="text-purple-400">({currentPtCode})</span>}
          {currentLlt && currentLlt !== currentPt && <span className="text-purple-500">← {currentLlt}</span>}
          {isNonCurrent && <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded font-extrabold">* Non-Current in 29.1 — Please Recode</span>}
          {currentSoc && <span className="text-slate-400 ml-1">· {currentSoc}</span>}
          <button type="button" onClick={onClear} className="ml-auto text-purple-300 hover:text-red-500 text-xs font-bold transition-colors">✕</button>
        </div>
      )}
      {!isActive ? (
        <button
          type="button"
          onClick={() => {
            setMeddraTarget({ section: targetSection, idx: targetIdx });
            setMeddraQuery("");
            setMeddraResults([]);
          }}
          className="text-xs font-bold text-purple-700 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg px-3 py-2 transition-colors"
        >
          {currentPt ? "✏️ Change MedDRA coding" : "🔎 Search MedDRA 29.1"}
        </button>
      ) : (
        <div className="relative">
          <div className="flex items-center gap-3 mb-1">
            <input
              autoFocus
              className="bg-white border border-purple-200 rounded-lg px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-purple-400 shadow-inner"
              placeholder="Type LLT, PT, or code (2+ chars)…"
              value={meddraQuery}
              onChange={e => searchMeddra(e.target.value, includeNonCurrent)}
            />
            {setIncludeNonCurrent && (
              <label className="flex items-center gap-1.5 text-xs text-slate-600 font-medium cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!includeNonCurrent}
                  onChange={e => {
                    setIncludeNonCurrent(e.target.checked);
                    if (meddraQuery.length >= 2) searchMeddra(meddraQuery, e.target.checked);
                  }}
                  className="accent-purple-600"
                />
                Include Non-current (*)
              </label>
            )}
            <button
              type="button"
              onClick={() => { setMeddraTarget(null); searchMeddra(""); }}
              className="text-slate-400 hover:text-red-500 text-sm font-bold px-2"
            >
              ✕
            </button>
          </div>
          {meddraResults.length > 0 && (
            <div className="absolute z-30 top-full left-0 right-0 bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl shadow-xl mt-1 max-h-56 overflow-y-auto">
              {meddraResults.map(m2 => {
                const nonCurr = m2.is_current === 0;
                return (
                  <div
                    key={m2.llt_code}
                    onClick={() => {
                      if (nonCurr) {
                        alert(`"${m2.llt}" is flagged as Non-Current (*) in MedDRA 29.1 and cannot be selected for new coding.`);
                        return;
                      }
                      pickMeddra(m2);
                    }}
                    className={`px-4 py-3 border-b border-slate-100 last:border-0 transition-colors ${nonCurr ? "bg-slate-100 opacity-60 cursor-not-allowed" : "hover:bg-purple-50 cursor-pointer"}`}
                  >
                    <div className="font-bold text-sm text-slate-800 flex items-center gap-2">
                      <span>{m2.pt}</span>
                      <span className="text-xs text-slate-400 font-normal">PT {m2.pt_code}</span>
                      {nonCurr && <span className="text-xs text-red-600 font-extrabold">* Non-Current</span>}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 mt-1">
                      <span>LLT: {m2.llt} ({m2.llt_code}){nonCurr ? " *" : ""}</span>
                      {m2.hlt && <span>HLT: {m2.hlt}</span>}
                      {m2.soc && <span className="text-purple-600 font-medium">{m2.soc}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!meddraLoading && meddraQuery.length >= 2 && meddraResults.length === 0 && (
            <div className="absolute z-30 top-full left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-md mt-1 px-4 py-3 text-sm text-slate-500 font-medium">
              No MedDRA 29.1 terms found for "{meddraQuery}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function SearchPanel({ cases, onOpenCase }) {
  const [qCase, setQCase] = useState("");
  const [qProduct, setQProduct] = useState("");
  const [qCountry, setQCountry] = useState("");

  const hasQuery = qCase.trim() || qProduct.trim() || qCountry.trim();

  const results = hasQuery ? cases.filter(c => {
    const caseNum = (c.caseNumber || c.id || "").toLowerCase();
    const country = (c.triage?.country || "").toLowerCase();
    const products = (c.products || []).map(p => (p.name || "") + " " + (p.genericName || "")).join(" ").toLowerCase();
    const matchCase = !qCase.trim() || caseNum.includes(qCase.trim().toLowerCase());
    const matchProduct = !qProduct.trim() || products.includes(qProduct.trim().toLowerCase());
    const matchCountry = !qCountry.trim() || country.includes(qCountry.trim().toLowerCase());
    return matchCase && matchProduct && matchCountry;
  }) : [];

  const stepColor = (step) => {
    const map = {
      1: "bg-blue-100 text-blue-800",
      2: "bg-teal-100 text-teal-800",
      3: "bg-purple-100 text-purple-800",
      4: "bg-orange-100 text-orange-800",
      5: "bg-violet-100 text-violet-800",
      6: "bg-slate-100 text-slate-700"
    };
    return map[step] || "bg-slate-100 text-slate-600";
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 w-full">
      <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white p-6 mb-6">
        <h3 className="text-sm font-extrabold text-slate-700 uppercase tracking-widest mb-5 flex items-center gap-2">🔍 Case Search</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Case Number</label>
            <input
              className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white w-full transition-all"
              placeholder="e.g. PV-2026-..."
              value={qCase}
              onChange={e => setQCase(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Product / Drug Name</label>
            <input
              className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white w-full transition-all"
              placeholder="Brand or generic name"
              value={qProduct}
              onChange={e => setQProduct(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Country of Incidence</label>
            <input
              className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white w-full transition-all"
              placeholder="e.g. India, Germany..."
              value={qCountry}
              onChange={e => setQCountry(e.target.value)}
            />
          </div>
        </div>
        {hasQuery && (
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">
              {results.length === 0 ? "No cases match your search." : `${results.length} case${results.length === 1 ? "" : "s"} found`}
            </span>
            <button
              onClick={() => { setQCase(""); setQProduct(""); setQCountry(""); }}
              className="text-xs text-slate-400 hover:text-red-500 transition-colors font-bold"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {hasQuery && results.length > 0 && (
        <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white overflow-hidden">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-indigo-50/50 border-b border-indigo-100">
                <th className="text-left px-5 py-4 text-xs font-extrabold text-indigo-900 uppercase tracking-wider">Case #</th>
                <th className="text-left px-5 py-4 text-xs font-extrabold text-indigo-900 uppercase tracking-wider">Aware Date</th>
                <th className="text-left px-5 py-4 text-xs font-extrabold text-indigo-900 uppercase tracking-wider">Country</th>
                <th className="text-left px-5 py-4 text-xs font-extrabold text-indigo-900 uppercase tracking-wider">Product(s)</th>
                <th className="text-left px-5 py-4 text-xs font-extrabold text-indigo-900 uppercase tracking-wider">MedDRA 29.1 PT(s)</th>
                <th className="text-left px-5 py-4 text-xs font-extrabold text-indigo-900 uppercase tracking-wider">Clock</th>
                <th className="text-left px-5 py-4 text-xs font-extrabold text-indigo-900 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.map((c) => {
                const t = c.triage || {};
                const ind = getDBWorkflowIndicator(c);
                return (
                  <tr
                    key={c.id}
                    onClick={() => onOpenCase(c)}
                    className="cursor-pointer hover:bg-indigo-50/50 transition-colors bg-white/50"
                  >
                    <td className="px-5 py-4 font-mono font-bold text-indigo-700 whitespace-nowrap">
                      {c.locked ? "🔒 " : ""}{c.caseNumber}
                    </td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap font-medium">{c.general?.awareDate || t.receiptDate || "-"}</td>
                    <td className="px-5 py-4 text-slate-700 font-medium">{t.country || "-"}</td>
                    <td className="px-5 py-4 font-bold text-slate-800">{(c.products || []).map(p => p.name).filter(Boolean).join(", ") || "-"}</td>
                    <td className="px-5 py-4 font-bold text-purple-700">{(c.events || []).map(e => `${e.pt || e.term}${e.non_current ? " *" : ""}`).filter(Boolean).join(", ") || "-"}</td>
                    <td className="px-5 py-4 text-xs font-bold">{ind.icon} {ind.label}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${stepColor(c.currentStep)}`}>
                        {c.status || "-"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!hasQuery && (
        <div className="text-center py-24">
          <div className="text-5xl mb-4 opacity-50 text-slate-400 drop-shadow-sm">🔍</div>
          <div className="text-sm font-bold text-slate-500">Enter a case number, product name, or country above to search.</div>
          <div className="text-xs text-slate-400 mt-2 font-medium">Searches across active, submitted, and archived cases.</div>
        </div>
      )}
    </div>
  );
}

/* ================= MAIN APPLICATION ================= */
export default function App() {
  const [user, setUser] = useState(null);
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [login, setLogin] = useState({ username: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("general");
  const [meddraQuery, setMeddraQuery] = useState("");
  const [meddraResults, setMeddraResults] = useState([]);
  const [meddraLoading, setMeddraLoading] = useState(false);
  const [includeNonCurrent, setIncludeNonCurrent] = useState(false);
  const [meddraInfo, setMeddraInfo] = useState({ version: "29.1", currentCount: 0, nonCurrentCount: 0 });
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeFile, setUpgradeFile] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeStats, setUpgradeStats] = useState(null);
  const [msg, setMsg] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const meddraDebounce = useRef(null);
  const [meddraTarget, setMeddraTarget] = useState(null);
  const [dupResults, setDupResults] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [scoreModalDup, setScoreModalDup] = useState(null);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [lockForm, setLockForm] = useState({ password: "", notes: "", followUpType: "significant" });
  const [showLineListing, setShowLineListing] = useState(false);
  const [activeView, setActiveView] = useState("dashboard");
  const [llFilter, setLlFilter] = useState("");
  const [llSeriousFilter, setLlSeriousFilter] = useState("all");

  const flash = useCallback((text, type = "ok") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  }, []);

  const fetchCases = useCallback(async () => {
    try {
      const res = await axios.get(API + "/cases");
      setCases(res.data || []);
    } catch {
      flash("Could not load cases — check backend connection.", "error");
    }
  }, [flash]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(API + "/health");
      if (res.data) {
        setMeddraInfo({
          version: res.data.meddraVersion || "29.1",
          currentCount: res.data.meddraCurrentCount || 0,
          nonCurrentCount: res.data.meddraNonCurrentCount || 0,
        });
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (user) {
      fetchCases();
      fetchHealth();
    }
  }, [user, fetchCases, fetchHealth]);

  const fetchAudit = async (caseId) => {
    try {
      const res = await axios.get(API + "/cases/" + caseId + "/audit");
      setAuditLog(res.data || []);
    } catch {
      setAuditLog([]);
    }
  };

  const resetSession = () => {
    setForm({});
    setSelected(null);
    setCases([]);
    setAuditLog([]);
    setShowAudit(false);
    setDupResults(null);
    setDupLoading(false);
    setScoreModalDup(null);
    setMeddraQuery("");
    setMeddraResults([]);
    setMeddraTarget(null);
    setTab("general");
    setMsg(null);
    setShowLineListing(false);
    setActiveView("dashboard");
  };

  const doLogin = async () => {
    const inputUser = login.username.toLowerCase().trim();
    const inputPass = login.password;
    if (!inputUser || !inputPass) {
      alert("Please enter both username and password.");
      return;
    }
    setLoginLoading(true);
    try {
      const res = await axios.post(API + "/auth/login", { username: inputUser, password: inputPass });
      resetSession();
      setUser(res.data);
      setLogin({ username: "", password: "" });
    } catch (err) {
      alert(err?.response?.data?.error || "Invalid credentials. Ensure format is basename_role (e.g., anand_triage).");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRunMeddraUpgrade = async () => {
    setUpgradeLoading(true);
    setUpgradeStats(null);
    try {
      const formData = new FormData();
      if (upgradeFile) {
        formData.append("file", upgradeFile);
      }
      const res = await axios.post(API + "/meddra/upgrade", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setUpgradeStats(res.data.stats);
      await fetchHealth();
      await fetchCases();
      flash("✅ MedDRA 29.1 dictionary upgrade & self-service case recoding complete!");
    } catch (err) {
      alert(err?.response?.data?.error || "MedDRA upgrade failed. Check backend logs.");
    } finally {
      setUpgradeLoading(false);
    }
  };

  const downloadE2B = async () => {
    if (!selected?.id) return;
    if (selected.currentStep < 3) {
      flash("⚠️ E2B(R3) XML is available from Medical Review (Step 3) onward.", "error");
      return;
    }
    try {
      flash("⏳ Generating ICH E2B(R3) XML…");
      const params = new URLSearchParams({ user: user?.username || "unknown", role: user?.role || "unknown" });
      const res = await axios.get(`${API}/cases/${selected.id}/e2b?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `E2B_${selected.caseNumber}${selected.locked ? "" : "_DRAFT"}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      fetchAudit(selected.id);
      flash(`📨 E2B(R3) XML downloaded (${(selected.locked || Number(selected.currentStep) >= 5) ? "Final" : "Draft"}) — ${selected.caseNumber}`);
    } catch {
      flash("❌ E2B export failed — check backend connection.", "error");
    }
  };

  const setNested = (section, key, value) =>
    setForm(f => ({ ...f, [section]: { ...(f[section] || {}), [key]: value } }));

  const setDeep = (section, subkey, key, value) =>
    setForm(f => ({
      ...f,
      [section]: {
        ...(f[section] || {}),
        [subkey]: { ...((f[section] || {})[subkey] || {}), [key]: value }
      }
    }));

  const isMyCase = (c) => c && user && c.currentStep === user.step;

  const autoSerious = () => getUnifiedSeriousness(form).isSerious;

  const copyPatientFromReporter = () => {
    const rep = form.general?.reporter || {};
    const initials = [rep.firstName?.[0], rep.lastName?.[0]].filter(Boolean).join(".").toUpperCase();
    setForm(f => ({
      ...f,
      triage: { ...(f.triage || {}), patientInitials: initials || f.triage?.patientInitials || "" },
      patient: { ...(f.patient || {}), initials: initials || f.patient?.initials || "" }
    }));
    flash("👤 Copied Patient Initials from Reporter Information.");
  };

  const detectDuplicate = async (silent = false) => {
    const t = form.triage || {};
    const drug = (form.products || [])[0]?.name || "";
    const event = (form.events || [])[0]?.term || "";
    const pt = (form.events || [])[0]?.pt || "";
    if (!drug && !event && !t.faersId) return false;
    setDupLoading(true);
    try {
      const res = await axios.post(API + "/cases/duplicate-check", {
        patientInitials: t.patientInitials || "",
        drugName: drug,
        eventTerm: event,
        eventPt: pt,
        country: t.country || "",
        onsetDate: (form.events || [])[0]?.onsetDate || "",
        faersId: t.faersId || "",
      });
      const { duplicates } = res.data;
      setDupLoading(false);
      if (duplicates && duplicates.length > 0) {
        setDupResults(duplicates);
        if (!silent) {
          const top = duplicates[0];
          const proceed = window.confirm(
            `⚠️ Smart Duplicate Search Alert!\n\nCase: ${top.caseNumber}\nMatch Score: ${top.score}% (${top.band})\nDrug: ${top.drug}\nEvent: ${top.event}\nCountry: ${top.country}\n\nClick OK to book-in as a new case anyway, or Cancel to review/append as Follow-up.`
          );
          return !proceed;
        }
        return false;
      } else {
        setDupResults([]);
      }
    } catch {
      setDupLoading(false);
    }
    return false;
  };

  const acceptDuplicateAsFollowUp = async (dupCaseId, dupCaseNumber) => {
    const justification = window.prompt(`Append incoming report as a Significant Follow-Up to ${dupCaseNumber}.\nEnter Follow-up Justification:`, "Additional follow-up information received at Triage");
    if (!justification) return;
    try {
      const res = await axios.post(`${API}/cases/${dupCaseId}/followup`, {
        performedBy: user.username,
        role: user.role,
        receivedDate: form.triage?.receiptDate || new Date().toISOString().slice(0, 10),
        significant: true,
        amendment: false,
        justification,
      });
      setForm({});
      setDupResults(null);
      await fetchCases();
      setSelected(res.data);
      setForm(res.data);
      fetchAudit(dupCaseId);
      flash(`🔄 Appended as Significant Follow-Up to ${dupCaseNumber}.`);
    } catch {
      flash("❌ Failed to append follow-up.", "error");
    }
  };

  const createCase = async () => {
    if (!form.triage?.receiptDate) { alert("Initial Receipt Date is required."); return; }
    if (!form.triage?.country) { alert("Country of Incidence is required."); return; }
    const blocked = await detectDuplicate(false);
    if (blocked) return;
    try {
      await axios.post(API + "/cases", {
        triage: form.triage || {},
        general: form.general || {},
        patient: form.patient || {},
        products: form.products || [],
        events: form.events || [],
        _audit: { performedBy: user.username, role: user.role }
      });
      setForm({});
      setDupResults(null);
      fetchCases();
      flash("✅ Case booked-in successfully.");
    } catch (err) {
      flash("❌ " + (err?.response?.data?.error || "Could not create case."), "error");
    }
  };

  const updateCase = async () => {
    if (!isMyCase(selected)) { alert("⛔ You can only submit cases assigned to your role step."); return; }
    const justification = window.prompt("Routing Justification / Comments (optional):", "Completed step review") ?? "";
    try {
      await axios.put(API + "/cases/" + selected.id, {
        ...form,
        _audit: { performedBy: user.username, role: user.role, justification }
      });
      setSelected(null);
      setForm({});
      fetchCases();
      flash("✅ Case routed to next workflow state.");
    } catch (err) {
      flash("❌ " + (err?.response?.data?.error || "Update failed."), "error");
    }
  };

  const returnCaseToDataEntry = async () => {
    if (!isMyCase(selected)) { alert("⛔ You can only route cases assigned to your role step."); return; }
    const reason = window.prompt("Return justification to Data Entry (required):");
    if (!reason) return;
    try {
      await axios.put(API + "/cases/" + selected.id, {
        ...form,
        medical: { ...(form.medical || {}), routeBackToDataEntry: true },
        _audit: { performedBy: user.username, role: user.role, justification: reason }
      });
      setSelected(null);
      setForm({});
      fetchCases();
      flash("↩️ Case returned to Data Entry.");
    } catch {
      flash("❌ Routing failed.", "error");
    }
  };

  const returnCaseToQuality = async () => {
    if (!isMyCase(selected)) { alert("⛔ You can only route cases assigned to your role step."); return; }
    const reason = window.prompt("Return reason to Quality Review (required):");
    if (!reason) return;
    try {
      await axios.put(API + "/cases/" + selected.id, {
        ...form,
        submissions: { ...(form.submissions || {}), routeBackToQuality: true, returnReason: reason },
        _audit: { performedBy: user.username, role: user.role, justification: reason }
      });
      setSelected(null);
      setForm({});
      fetchCases();
      flash("↩️ Case returned to Quality Review.");
    } catch {
      flash("❌ Routing failed.", "error");
    }
  };

  const saveTab = async (fields, label = "tab") => {
    if (!selected?.id) { flash("No case selected.", "error"); return; }
    if (selected.locked) { flash("🔒 Case is globally locked. Unlock before saving edits.", "error"); return; }
    try {
      const res = await axios.patch(API + "/cases/" + selected.id, {
        ...fields,
        _audit: { performedBy: user.username, role: user.role, tabLabel: label }
      });
      // Keep the exact fields just submitted in local state even if the API response
      // omits nested fields or returns an older copy of the case.
      setForm(f => ({ ...f, ...res.data, ...fields }));
      setSelected(s => ({ ...s, ...res.data, ...fields }));
      fetchCases();
      fetchAudit(selected.id);
      flash("💾 " + label + " saved.");
    } catch (err) {
      flash("❌ " + (err?.response?.data?.error || "Save failed."), "error");
    }
  };

  const submitCaseLockToggle = async () => {
    if (!selected?.id) return;
    if (Number(selected.currentStep) !== 5 || Number(user?.step) !== 5 || String(user?.role || "").toLowerCase() !== "submissions") {
      flash("Case lock/unlock is restricted to the Submissions role at Step 5.", "error");
      setLockModalOpen(false);
      return;
    }
    try {
      const res = await axios.post(`${API}/cases/${selected.id}/lock`, {
        username: user.username,
        role: user.role,
        password: lockForm.password,
        notes: lockForm.notes,
        followUpType: selected.locked ? lockForm.followUpType : null,
      });
      setSelected(res.data);
      setForm(res.data);
      setLockModalOpen(false);
      setLockForm({ password: "", notes: "", followUpType: "significant" });
      fetchCases();
      fetchAudit(selected.id);
      flash(res.data.locked ? "🔒 Case Globally Locked." : "🔓 Case Unlocked for Follow-Up / Editing.");
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to toggle case lock.");
    }
  };

  const searchMeddra = (q, incNonCurr = includeNonCurrent) => {
    setMeddraQuery(q);
    if (meddraDebounce.current) clearTimeout(meddraDebounce.current);
    if (q.length < 2) { setMeddraResults([]); setMeddraLoading(false); return; }
    setMeddraLoading(true);
    meddraDebounce.current = setTimeout(async () => {
      try {
        const res = await axios.get(API + "/meddra/search", {
          params: { q, includeNonCurrent: incNonCurr ? "true" : "false", limit: 25 }
        });
        setMeddraResults(res.data || []);
      } catch {
        setMeddraResults([]);
        flash("MedDRA search unavailable.", "error");
      } finally {
        setMeddraLoading(false);
      }
    }, 280);
  };

  const pickMeddra = (m) => {
    const target = meddraTarget;
    if (!target) return;

    if (target.section === "medical_event" || target.section === "event") {
      const events = [...((form.events?.length) ? form.events : [{}])];
      const idx = target.idx || 0;
      const isIme = IME_TERMS.some(t => t.toLowerCase() === (m.pt || "").toLowerCase());
      events[idx] = {
        ...events[idx],
        llt: m.llt, llt_code: m.llt_code, pt: m.pt, pt_code: m.pt_code,
        hlt: m.hlt, hlgt: m.hlgt, soc: m.soc, meddra_version: m.version || "29.1",
        non_current: false,
        term: events[idx]?.term || m.llt,
        seriousness: isIme ? { ...(events[idx]?.seriousness || {}), medSignificant: true } : events[idx]?.seriousness
      };
      setForm(f => ({ ...f, events }));
    } else if (target.section === "lab") {
      const labData = [...((form.patient?.labData?.length) ? form.patient.labData : [{}])];
      labData[target.idx] = { ...labData[target.idx], meddraPt: m.pt, meddraPtCode: m.pt_code, meddraLlt: m.llt, meddraHlt: m.hlt, meddraSoc: m.soc };
      setForm(f => ({ ...f, patient: { ...f.patient, labData } }));
    } else if (target.section === "history") {
      const otherHistory = [...((form.patient?.otherHistory?.length) ? form.patient.otherHistory : [{}])];
      otherHistory[target.idx] = { ...otherHistory[target.idx], meddraPt: m.pt, meddraPtCode: m.pt_code, meddraLlt: m.llt, meddraHlt: m.hlt, meddraSoc: m.soc };
      setForm(f => ({ ...f, patient: { ...f.patient, otherHistory } }));
    } else if (target.section === "indication") {
      const products = [...((form.products?.length) ? form.products : [{}])];
      products[target.idx] = { ...products[target.idx], indicationPt: m.pt, indicationPtCode: m.pt_code, indicationLlt: m.llt, indicationSoc: m.soc };
      setForm(f => ({ ...f, products }));
    } else if (target.section === "medHistory") {
      setForm(f => ({ ...f, patient: { ...f.patient, medHistoryPt: m.pt, medHistoryPtCode: m.pt_code, medHistoryLlt: m.llt, medHistorySoc: m.soc } }));
    }

    setMeddraQuery("");
    setMeddraResults([]);
    setMeddraLoading(false);
    setMeddraTarget(null);
  };

  const runWHOUMC = (idx) => {
    setForm(f => {
      const events = [...(f.events || [{}])];
      const e = events[idx] || {};
      let result = "Unassessable";
      if (e.rechallenge) result = "Certain";
      else if (e.temporal && e.dechallenge && !e.alternative) result = "Probable";
      else if (e.temporal && !e.alternative) result = "Possible";
      else if (e.temporal) result = "Unlikely";
      events[idx] = { ...e, causality: result };
      return { ...f, events };
    });
  };

  const runNaranjo = (idx) => {
    setForm(f => {
      const events = [...(f.events || [{}])];
      const e = events[idx] || {};
      let score = 0;
      if (e.nar_previous) score += 1;
      if (e.nar_reaction) score += 2;
      if (e.nar_dechallenge) score += 1;
      if (e.nar_rechallenge) score += 2;
      if (e.nar_alternative) score -= 1;
      if (e.nar_placebo) score += 1;
      if (e.nar_drug_level) score += 1;
      if (e.nar_dose_related) score += 1;
      if (e.nar_prior_exp) score += 1;
      if (e.nar_confirmed) score += 1;
      // SkyVigilance uses a maximum displayed Naranjo score of 7.
      score = Math.min(7, score);
      const cat = score >= 7 ? "Definite" : score >= 5 ? "Probable" : score >= 1 ? "Possible" : "Doubtful";
      events[idx] = { ...e, naranjScore: score, naranjResult: cat, causality: cat };
      return { ...f, events };
    });
  };

  // Format ISO/date-input values as DD-MMM-YYYY for narrative text only.
  const formatNarrativeDate = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    let year, month, day;
    if (match) { year = Number(match[1]); month = Number(match[2]); day = Number(match[3]); }
    else {
      const alt = raw.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/);
      if (!alt) return raw;
      // Non-ISO slash/dot dates are interpreted as DD/MM/YYYY.
      day = Number(alt[1]); month = Number(alt[2]); year = Number(alt[3]);
    }
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (month < 1 || month > 12 || day < 1 || day > 31) return raw;
    return `${String(day).padStart(2, "0")}-${months[month - 1]}-${year}`;
  };

  const buildMedHistoryText = (p) => {
    const parts = [];
    if (p.medHistory) parts.push(String(p.medHistory).replace(/\s*\[MedDRA[^\]]*\]/gi, "").trim());
    (p.otherHistory || []).filter(h => h.description || h.substance || h.meddraPt).forEach(h => {
      // Use the clinical term as plain text; do not expose MedDRA coding metadata in the narrative.
      let entry = h.description || h.substance || h.meddraPt || "";
      if (h.duration) entry += ` (duration: ${h.duration})`;
      if (h.startDate) {
        entry += ` (from ${formatNarrativeDate(h.startDate)}`;
        if (h.stopDate) entry += ` to ${formatNarrativeDate(h.stopDate)}`;
        else if (h.ongoing) entry += ", ongoing";
        entry += ")";
      }
      if (entry.trim()) parts.push(entry.trim());
    });
    if (p.medHistoryPt && !parts.some(s => s.toLowerCase().includes(String(p.medHistoryPt).toLowerCase())))
      parts.push(String(p.medHistoryPt));
    return parts.filter(Boolean).join("; ") || "no relevant past history";
  };

  const generateNarrative = () => {
    const caseId = selected?.caseNumber || selected?.id || "[Case ID]";
    const p = form.patient || {};
    const products = form.products || [{}];
    const events = form.events || [{}];
    const t = form.triage || {};
    const ser = getUnifiedSeriousness(form);

    const medHistoryDisplay = buildMedHistoryText(p);
    const concomText = p.concomitant ? `Concomitant medications: ${p.concomitant}. ` : "";
    // Read lab rows from the patient section and tolerate legacy field names.
    const labRows = Array.isArray(p.labData) ? p.labData : (Array.isArray(form.labData) ? form.labData : []);
    const labs = labRows.filter(l => {
      const test = l.testName || l.test || l.name || l.labTest || l.investigation;
      return test && [l.result, l.value, l.abnormality, l.assessment, l.meddraPt, l.units, l.referenceRange].some(v => v !== undefined && v !== null && String(v).trim() !== "");
    });
    const labText = labs.length > 0
      ? `Relevant laboratory findings: ${labs.map(l => {
          const testName = l.testName || l.test || l.name || l.labTest || l.investigation;
          const result = l.result ?? l.value ?? "";
          const resultText = String(result).trim() ? `: ${result}${l.units ? " " + l.units : ""}` : "";
          const abnormality = l.abnormality && !["Not assessed", "Normal"].includes(l.abnormality) ? l.abnormality : "";
          const assessment = l.assessment && l.assessment !== "Normal" && l.assessment !== abnormality ? l.assessment : "";
          const clinicalFlag = [abnormality, assessment].filter(Boolean).join("; ");
          const rangeValue = l.referenceRange || l.refRange || l.normalRange;
          const range = rangeValue ? ` (reference range: ${rangeValue})` : "";
          const dateValue = l.date || l.collectionDate || l.collectedDate;
          const date = dateValue ? `, collected ${formatNarrativeDate(dateValue)}` : "";
          const meddra = l.meddraPt ? `, MedDRA PT: ${l.meddraPt}` : "";
          return `${testName}${resultText}${clinicalFlag ? ` (${clinicalFlag})` : ""}${range}${date}${meddra}`;
        }).join("; ")}. `
      : "";

    // 1 & 3: Handle Suspect Product Therapy Dates & Ongoing Regimens
    const productsText = products.map(d => {
      const productName = d.name || "[drug]";
      const doseRoute = `(${d.dose || "dose not reported"}${d.doseUnit ? " " + d.doseUnit : ""}, ${d.route || "route not reported"})`;
      const indication = `for ${d.indicationPt || d.indication || "[indication]"}`;
      
      let therapyDates = "";
      if (d.startDate || d.stopDate || d.ongoing) {
        const start = d.startDate ? formatNarrativeDate(d.startDate) : "start date not reported";
        // Distinguish reported stop date from ongoing treatment
        const end = d.stopDate 
          ? `stopped on ${formatNarrativeDate(d.stopDate)}` 
          : (d.ongoing ? "ongoing treatment" : "stop date not reported");
        therapyDates = `; treatment period: ${start} to ${end}`;
      }
      return `${productName} ${doseRoute} ${indication}${therapyDates}`;
    }).join(" and ");

    // 2: Handle Adverse Event Onset and Stop / End Dates
    const eventsText = events.map(e => {
      const eventName = `${e.pt || e.term || "[event]"}${e.diagSymptom === "D" ? " [Diagnosis]" : e.diagSymptom === "S" ? " [Symptom]" : ""}`;
      const onset = e.onsetDate ? formatNarrativeDate(e.onsetDate) : "onset date not reported";
      const stop = e.stopDate ? `stopped on ${formatNarrativeDate(e.stopDate)}` : "stop date not reported";
      const outcome = e.outcome ? ` (Outcome: ${e.outcome})` : "";
      return `${eventName} (onset: ${onset}; ${stop})${outcome}`;
    }).join("; ");

    const onsetDates = [...new Set(events.map(e => e.onsetDate ? formatNarrativeDate(e.onsetDate) : "").filter(Boolean))];
    const onsetText = onsetDates.length > 0 ? onsetDates.join(", ") : "[onset date not reported]";

    const seriousCriteria = ["death", "lifeThreatening", "hospitalisation", "disability", "congenital", "medSignificant"]
      .filter(k => ser[k]);
    const seriousStr = ser.isSerious ? `serious (${seriousCriteria.join(", ")})` : "non-serious";

    const causalityArray = events.filter(e => e.causality).map(e => `${e.pt || e.term}: ${e.causality}`);
    const causalityStr = causalityArray.length > 0 ? `Causality was assessed as ${causalityArray.join("; ")}. ` : "";

    const listednessArray = events.filter(e => e.listedness).map(e => `${e.pt || e.term}: ${e.listedness}`);
    const listednessStr = listednessArray.length > 0 ? `Listedness was determined as ${listednessArray.join("; ")}. ` : "";

    const fuCount = (form.followUps || []).length;
    const fuStr = fuCount > 0 ? `Follow-up #${fuCount} received on ${formatNarrativeDate(form.general?.awareDate) || "record"}. ` : "";

    const text =
      `Case ID: ${caseId}. ${fuStr}` +
      `A ${p.age || "[age]"}-${(p.ageUnit || "year").toLowerCase()}-old ${p.sex || "[sex]"} patient${p.weight ? " (" + p.weight + " kg)" : ""} ` +
      `with a medical history of ${medHistoryDisplay} ` +
      `was receiving ${productsText}. ` + concomText +
      `Event onset date(s): ${onsetText}. The reported event(s): ${eventsText}. ` +
      `The event was considered ${seriousStr}. ` + labText +
      causalityStr + listednessStr +
      `The case was reported by a ${t.qualification || "reporter"} from ${t.country || "[country]"}.`;

    setForm(f => ({ ...f, narrative: text }));
  };
  /* ================= PDF EXPORTS ================= */
  const exportCIOMS = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const PW = 210, PH = 297, M = 10, CW = PW - M * 2;
    const p = form.patient || {}, products = form.products || [{}], events = form.events || [{}];
    const d = products[0] || {}, ev = events[0] || {};
    const g = form.general || {}, t = form.triage || {};
    const ser = getUnifiedSeriousness(form);
    const val = (v) => String(v || ""), na = (v) => v ? String(v) : "—";
    const box = (x, y, w, h) => { doc.setDrawColor(100); doc.rect(x, y, w, h); };
    const hdr = (text, x, y, w, h = 5, fill = "dark") => {
      doc.setFillColor(fill === "dark" ? 26 : 240, fill === "dark" ? 58 : 240, fill === "dark" ? 92 : 240);
      doc.rect(x, y, w, h, "F");
      doc.setTextColor(fill === "dark" ? 255 : 40, fill === "dark" ? 255 : 40, fill === "dark" ? 255 : 40);
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.text(text, x + 2, y + 3.5); doc.setTextColor(0, 0, 0);
    };
    const lbl = (text, x, y) => { doc.setFont("helvetica", "bold"); doc.setFontSize(6.5); doc.setTextColor(80, 80, 80); doc.text(text, x, y); doc.setTextColor(0, 0, 0); };
    const fld = (text, x, y, maxW) => { doc.setFont("helvetica", "normal"); doc.setFontSize(8); const ln = doc.splitTextToSize(val(text), maxW); doc.text(ln, x, y); };
    const chk = (checked, x, y) => { box(x, y - 2.5, 3, 3); if (checked) { doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.text("X", x + 0.4, y); } };
    let y = M;
    const ensureSpace = (h) => { if (y + h > PH - 12) { doc.addPage(); y = M; } };

    const isDraft = !(selected?.locked || Number(selected?.currentStep) >= 5);
    const patInitDisplay = p.protectConfidentiality ? "PRIVACY" : (p.initials || t.patientInitials || "—");
    const fuCount = (form.followUps || []).length;

    doc.setFillColor(26, 58, 92); doc.rect(M, y, CW, 9, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(`CIOMS I FORM — SUSPECT ADVERSE REACTION REPORT ${isDraft ? "[DRAFT]" : "[FINAL]"}`, M + 2, y + 6);
    doc.setTextColor(0, 0, 0); y += 10;
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(120, 120, 120);
    doc.text(`Case No: ${na(selected?.caseNumber)}   |   Aware Date: ${na(g.awareDate || t.receiptDate)}   |   MedDRA: 29.1   |   Version: ${fuCount > 0 ? "F/U #" + fuCount : "Initial"}`, M, y + 3);
    doc.setTextColor(0, 0, 0); y += 6;

    hdr("I.  REACTION INFORMATION", M, y, CW); y += 6;
    box(M, y, 38, 14); lbl("1. PATIENT INITIALS", M + 1, y + 3.5); fld(patInitDisplay, M + 1, y + 10, 36);
    box(M + 38, y, 40, 14); lbl("1a. COUNTRY", M + 39, y + 3.5); fld(t.country, M + 39, y + 10, 38);
    box(M + 78, y, 30, 14); lbl("2. DATE OF BIRTH", M + 79, y + 3.5); fld(p.protectConfidentiality ? "PRIVACY" : (p.dob || "—"), M + 79, y + 10, 28);
    box(M + 108, y, 22, 14); lbl("2a. AGE", M + 109, y + 3.5); fld(p.age ? `${p.age} ${p.ageUnit || "yrs"}` : "—", M + 109, y + 10, 20);
    box(M + 130, y, 60, 14); lbl("3. SEX", M + 131, y + 3.5);
    chk(p.sex === "Male", M + 131, y + 10); doc.setFontSize(7); doc.text("Male", M + 135, y + 10);
    chk(p.sex === "Female", M + 146, y + 10); doc.text("Female", M + 150, y + 10);
    chk(!p.sex || p.sex === "Unknown", M + 162, y + 10); doc.text("Unknown", M + 166, y + 10); y += 15;

    box(M, y, 95, 12); lbl("4-6. REACTION ONSET DATE", M + 1, y + 3.5);
    const op = (ev.onsetDate || "").split("-"); fld(op.length === 3 ? op[2] + "/" + op[1] + "/" + op[0] : na(ev.onsetDate), M + 1, y + 10, 93);
    box(M + 95, y, 95, 12); lbl("7+13. REACTION STOP / RECOVERY DATE", M + 96, y + 3.5);
    const sp = (ev.stopDate || "").split("-"); fld(sp.length === 3 ? sp[2] + "/" + sp[1] + "/" + sp[0] : "—", M + 96, y + 10, 93); y += 13;

    box(M, y, CW, 12); lbl("8-12. CHECK ALL APPROPRIATE:", M + 1, y + 4);
    chk(ser.death, M + 1, y + 10); doc.setFontSize(6.5); doc.text("Patient Died", M + 5, y + 10);
    chk(ser.hospitalisation, M + 35, y + 10); doc.text("Hospitalisation", M + 39, y + 10);
    chk(ser.disability, M + 90, y + 10); doc.text("Disability", M + 94, y + 10);
    chk(ser.lifeThreatening, M + 145, y + 10); doc.text("Life Threatening", M + 149, y + 10); y += 13;

    const allEventsDesc = events.map((eItem, idx) =>
      `${idx + 1}. ${eItem.diagSymptom === "D" ? "[DIAGNOSIS] " : eItem.diagSymptom === "S" ? "[SYMPTOM] " : ""}${eItem.term || "—"} -> MedDRA 29.1 PT: ${eItem.pt || "Not coded"} (${eItem.pt_code || "—"}) | Outcome: ${eItem.outcome || "—"}`
    ).join("\n");
    const labs = (form.patient?.labData || []).filter(l => l.testName);
    const labLine = labs.length > 0 ? "\nLab: " + labs.map(l => `${l.testName} ${l.result || ""} ${l.units || ""}`).join("; ") : "";
    box(M, y, CW, 28); lbl("DESCRIBE REACTION(S) (MEDDRA 29.1):", M + 1, y + 4);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(doc.splitTextToSize((allEventsDesc + labLine) || "—", CW - 4).slice(0, 5), M + 1, y + 9); y += 29;

    hdr("II.  SUSPECT DRUG(S) INFORMATION", M, y, CW); y += 6;
    const allDrugsNames = products.map(pr => (pr.name || "—") + (pr.genericName ? " / " + pr.genericName : "")).join("; ");
    box(M, y, 70, 12); lbl("14. SUSPECT DRUG(S)", M + 1, y + 3.5); fld(allDrugsNames, M + 1, y + 10, 68);
    box(M + 70, y, 35, 12); lbl("15. DAILY DOSE(S)", M + 71, y + 3.5); fld((d?.dose || "—") + " " + (d?.doseUnit || ""), M + 71, y + 10, 33);
    box(M + 105, y, 85, 12); lbl("16. ROUTE", M + 106, y + 3.5); fld(d?.route || "—", M + 106, y + 10, 83); y += 13;
    box(M, y, 70, 12); lbl("17. INDICATION", M + 1, y + 3.5); fld(d?.indicationPt || d?.indication || "—", M + 1, y + 8, 68);
    box(M + 70, y, 60, 12); lbl("18. THERAPY DATES", M + 71, y + 3.5); fld((d?.startDate || "—") + " to " + (d?.stopDate || (d?.ongoing ? "Ongoing" : "—")), M + 71, y + 10, 58);
    box(M + 130, y, 60, 12); lbl("19. DURATION", M + 131, y + 3.5); fld(d?.duration || calcDaysDiff(d?.startDate, d?.stopDate) || "—", M + 131, y + 10, 58); y += 13;

    hdr("III.  CONCOMITANT DRUG(S) AND HISTORY", M, y, CW); y += 6;
    box(M, y, CW, 18); lbl("22. CONCOMITANT DRUG(S):", M + 1, y + 4);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(doc.splitTextToSize(p.concomitant || "None reported", CW - 4).slice(0, 3), M + 1, y + 9); y += 19;

    const histText23 = buildMedHistoryText(p);
    box(M, y, CW, 22); lbl("23. OTHER RELEVANT HISTORY:", M + 1, y + 4);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(doc.splitTextToSize(histText23, CW - 4).slice(0, 4), M + 1, y + 9); y += 23;

    const narrativeText = form.narrative || "";
    if (narrativeText) {
      ensureSpace(45);
      hdr("CASE NARRATIVE", M, y, CW); y += 6;
      const avail = PH - y - 15, boxH = Math.min(65, Math.max(25, avail));
      box(M, y, CW, boxH);
      const narLn = doc.splitTextToSize(narrativeText, CW - 4);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8);
      doc.text(narLn.slice(0, Math.floor(boxH / 4.5)), M + 1, y + 5);
    }

    doc.save(`CIOMS_I_${selected?.caseNumber || "case"}${isDraft ? "_DRAFT" : ""}.pdf`);
  };

  const exportMedWatch = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const PW = 210, M = 10, CW = PW - M * 2;
    const p = form.patient || {}, ev = (form.events || [{}])[0] || {};
    const g = form.general || {}, t = form.triage || {};
    const ser = getUnifiedSeriousness(form);
    const val = (v) => v ? String(v) : "";
    const na = (v) => v ? String(v) : "—";
    const box = (x, y, w, h) => { doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.3); doc.rect(x, y, w, h); };
    const hdrBlue = (text, x, y, w, h = 6) => {
      doc.setFillColor(0, 102, 204); doc.rect(x, y, w, h, "F");
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(8);
      doc.text(text, x + 2, y + 4); doc.setTextColor(0, 0, 0);
    };
    const lbl = (text, x, y) => { doc.setFont("helvetica", "bold"); doc.setFontSize(6); doc.setTextColor(60, 60, 60); doc.text(text, x, y); doc.setTextColor(0, 0, 0); };
    const fld = (text, x, y, maxW) => { doc.setFont("helvetica", "normal"); doc.setFontSize(8); if (!text) return; const ln = doc.splitTextToSize(String(text), maxW); doc.text(ln, x, y); };
    const chkBox = (checked, x, y) => { box(x, y - 2.5, 3, 3); if (checked) { doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text("X", x + 0.3, y); } };
    let y = M;
    const isDraft = !(selected?.locked || Number(selected?.currentStep) >= 5);

    doc.setFillColor(0, 102, 204); doc.rect(M, y, CW, 16, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text("U.S. DEPARTMENT OF HEALTH AND HUMAN SERVICES", M + 2, y + 5);
    doc.text("Food and Drug Administration", M + 2, y + 10);
    doc.setFontSize(12); doc.text(`MEDWATCH — FORM FDA 3500 ${isDraft ? "[DRAFT]" : "[FINAL]"}`, M + 85, y + 11);
    doc.setTextColor(0, 0, 0); y += 18;

    hdrBlue("A.  PATIENT INFORMATION", M, y, CW); y += 7;
    const initials = p.protectConfidentiality ? "PRIVACY" : val(p.initials || t.patientInitials);
    box(M, y, 52, 15); lbl("1. Patient Identifier", M + 1, y + 3.5); fld(initials || "—", M + 1, y + 11, 50);
    box(M + 52, y, 25, 15); lbl("2. Age at onset", M + 53, y + 3.5); fld(p.age ? `${p.age} ${p.ageUnit || "yrs"}` : "—", M + 53, y + 11, 23);
    box(M + 77, y, 33, 15); lbl("3. Sex", M + 78, y + 3.5);
    chkBox(p.sex === "Male", M + 78, y + 11); doc.setFontSize(7); doc.text("Male", M + 82, y + 11);
    chkBox(p.sex === "Female", M + 95, y + 11); doc.text("Female", M + 99, y + 11);
    box(M + 110, y, 30, 15); lbl("4. Weight", M + 111, y + 3.5); fld(p.weight ? p.weight + " kg" : "—", M + 111, y + 11, 28);
    box(M + 140, y, 50, 15); lbl("5. Race / Ethnicity", M + 141, y + 3.5); fld(p.ethnicity || "—", M + 141, y + 11, 48); y += 16;

    hdrBlue("B.  ADVERSE EVENT, PRODUCT PROBLEM", M, y, CW); y += 7;
    box(M, y, 80, 22); lbl("1. Type of Report", M + 1, y + 4);
    chkBox(true, M + 1, y + 11); doc.setFontSize(7); doc.text("Adverse Event", M + 5, y + 11);
    box(M + 80, y, 110, 22); lbl("2. Outcomes (check all that apply)", M + 81, y + 4);
    chkBox(ser.death, M + 81, y + 11); doc.setFontSize(7); doc.text("Death", M + 85, y + 11);
    chkBox(ser.lifeThreatening, M + 81, y + 17); doc.text("Life-Threatening", M + 85, y + 17);
    chkBox(ser.hospitalisation, M + 120, y + 11); doc.text("Hospitalization", M + 124, y + 11);
    chkBox(ser.disability, M + 120, y + 17); doc.text("Disability", M + 124, y + 17);
    chkBox(ser.congenital, M + 155, y + 11); doc.text("Congenital Anomaly", M + 159, y + 11);
    chkBox(ser.medSignificant, M + 155, y + 17); doc.text("Other Med. Signif.", M + 159, y + 17); y += 23;

    box(M, y, 95, 12); lbl("3. Date of Adverse Event", M + 1, y + 3.5); fld(na(ev.onsetDate), M + 1, y + 10, 93);
    box(M + 95, y, 95, 12); lbl("4. Date of this Report", M + 96, y + 3.5); fld(new Date().toLocaleDateString("en-GB"), M + 96, y + 10, 93); y += 13;

    const narrativeText = form.narrative || ev.term || "—";
    const narLn2 = doc.splitTextToSize(narrativeText, CW - 4);
    box(M, y, CW, 45); lbl("5. Describe Event or Problem:", M + 1, y + 4);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(narLn2.slice(0, 8), M + 1, y + 10); y += 47;

    hdrBlue("G.  REPORTER", M, y, CW); y += 7;
    const repName = g.reporterAnonymous
      ? "NAME AND ADDRESS WITHHELD"
      : ([val(g.reporter?.firstName), val(g.reporter?.lastName)].filter(Boolean).join(" ") || "—");
    const repInst = g.reporterAnonymous ? "WITHHELD" : (val(g.reporter?.institution) || "—");
    box(M, y, 95, 12); lbl("1. Name", M + 1, y + 3.5); fld(repName, M + 1, y + 10, 93);
    box(M + 95, y, 95, 12); lbl("2. Institution", M + 96, y + 3.5); fld(repInst, M + 96, y + 10, 93);

    doc.save(`MedWatch_3500_${selected?.caseNumber || "case"}${isDraft ? "_DRAFT" : ""}.pdf`);
  };

  /* ================= LINE LISTING ================= */
  const lineListingRows = () => cases.map(c => {
    const t = c.triage || {};
    const g = c.general || {};
    const p = c.patient || {};
    const products = c.products || [{}];
    const events = c.events || [{}];
    const ser = getUnifiedSeriousness(c);
    const ind = getDBWorkflowIndicator(c);
    const fuCount = (c.followUps || []).length;

    const seriousFlags = [
      ser.death && "Death",
      ser.lifeThreatening && "Life-threatening",
      ser.hospitalisation && "Hospitalisation",
      ser.disability && "Disability",
      ser.congenital && "Congenital anomaly",
      ser.medSignificant && "Medically significant"
    ].filter(Boolean);

    return {
      id: c.id,
      caseNumber: c.caseNumber,
      locked: c.locked,
      receiptDate: g.awareDate || t.receiptDate || "—",
      followUpTag: fuCount > 0 ? `F/U #${fuCount}` : "Initial",
      fLtTag: ser.fLtTag,
      clockLabel: `${ind.icon} ${ind.label}`,
      country: t.country || "—",
      reportType: t.reportType || g.reportType || "—",
      patientInitials: p.protectConfidentiality ? "PRIVACY" : (p.initials || t.patientInitials || "—"),
      age: p.protectConfidentiality ? "PRIVACY" : (p.age ? `${p.age} ${p.ageUnit || "yrs"}` : "—"),
      sex: p.sex || "—",
      drug: products.map(pr => pr.name).filter(Boolean).join("; ") || "—",
      genericName: products.map(pr => pr.genericName).filter(Boolean).join("; ") || "—",
      dose: products.map(pr => pr.dose ? `${pr.dose} ${pr.doseUnit || ""}`.trim() : "").filter(Boolean).join("; ") || "—",
      route: products.map(pr => pr.route).filter(Boolean).join("; ") || "—",
      indication: products.map(pr => pr.indicationPt || pr.indication).filter(Boolean).join("; ") || "—",
      startDate: products[0]?.startDate || "—",
      stopDate: products[0]?.ongoing ? "Ongoing" : (products[0]?.stopDate || "—"),
      eventVerbatim: events.map(ev => ev.term).filter(Boolean).join("; ") || "—",
      pt: events.map(ev => `${ev.pt || ""}${ev.non_current ? " *" : ""}`).filter(Boolean).join("; ") || "—",
      ptCode: events.map(ev => ev.pt_code).filter(Boolean).join("; ") || "—",
      soc: events.map(ev => ev.soc).filter(Boolean).join("; ") || "—",
      onsetDate: events[0]?.onsetDate || "—",
      serious: seriousFlags.length ? seriousFlags.join("; ") : "Non-serious",
      causality: events.map(ev => ev.causality).filter(Boolean).join("; ") || "—",
      listedness: events.map(ev => ev.listedness).filter(Boolean).join("; ") || "—",
      outcome: events.map(ev => ev.outcome).filter(Boolean).join("; ") || "—",
      status: c.status || "—",
      reporter: g.reporterAnonymous ? "WITHHELD" : (t.qualification || g.reporter?.qualification || "—"),
    };
  });

  const exportLineListing = (format) => {
    const rows = lineListingRows();
    const headers = [
      "Case #", "Aware Date", "Version", "F/LT/H", "Country", "Report Type", "Patient Initials", "Age", "Sex",
      "Drug (Brand)", "Generic Name", "Dose", "Route", "Indication", "Drug Start", "Drug Stop",
      "Event Verbatim", "MedDRA 29.1 PT", "PT Code", "SOC", "Onset Date",
      "Seriousness", "Causality", "Listedness", "Outcome", "Status", "Reporter Qualification"
    ];
    const keys = [
      "caseNumber", "receiptDate", "followUpTag", "fLtTag", "country", "reportType", "patientInitials", "age", "sex",
      "drug", "genericName", "dose", "route", "indication", "startDate", "stopDate",
      "eventVerbatim", "pt", "ptCode", "soc", "onsetDate",
      "serious", "causality", "listedness", "outcome", "status", "reporter"
    ];

    if (format === "csv") {
      const escapeCsv = v => `"${String(v).replace(/"/g, '""')}"`;
      const csv = [headers.map(escapeCsv).join(","), ...rows.map(r => keys.map(k => escapeCsv(r[k])).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `LineListing_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      flash("📊 Line listing exported as CSV.");
    } else if (format === "pdf") {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
      const PW = 420, M = 10, PH_A3 = 297;
      doc.setFillColor(30, 58, 138); doc.rect(M, M, PW - M * 2, 10, "F");
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("SkyVigilance SafetyDB — CIOMS II / ICSR Line Listing (MedDRA 29.1)", M + 2, M + 7);
      doc.setFontSize(7);
      doc.text(`Query Executed: ${new Date().toLocaleString("en-GB")} | Total cases: ${rows.length}`, PW - M - 95, M + 7);
      doc.setTextColor(0, 0, 0);

      const colW = [16, 16, 12, 10, 16, 14, 12, 10, 8, 20, 16, 12, 12, 16, 14, 14, 20, 18, 10, 16, 14, 22, 14, 14, 16, 14, 14];
      let x = M, y = M + 14;
      const drawHeader = (startY) => {
        let hx = M;
        headers.forEach((h, i) => {
          doc.setFillColor(230, 232, 240);
          doc.rect(hx, startY, colW[i], 8, "F");
          doc.setDrawColor(180, 180, 200);
          doc.rect(hx, startY, colW[i], 8);
          doc.setFont("helvetica", "bold"); doc.setFontSize(5.2);
          doc.text(h, hx + 1, startY + 5, { maxWidth: colW[i] - 2 });
          hx += colW[i];
        });
      };
      drawHeader(y);
      y += 9;

      rows.forEach((r, ri) => {
        if (y > PH_A3 - 22) { doc.addPage(); y = M; drawHeader(y); y += 9; }
        if (ri % 2 === 0) {
          doc.setFillColor(248, 249, 255); x = M;
          keys.forEach((_, i) => { doc.rect(x, y, colW[i], 7, "F"); x += colW[i]; });
        }
        x = M;
        keys.forEach((k, i) => {
          doc.setDrawColor(210, 210, 225); doc.rect(x, y, colW[i], 7);
          doc.setFont("helvetica", "normal"); doc.setFontSize(5);
          doc.text(String(r[k]).slice(0, 32), x + 1, y + 4.5, { maxWidth: colW[i] - 2 });
          x += colW[i];
        });
        y += 7;
      });
      doc.save(`LineListing_${new Date().toISOString().slice(0, 10)}.pdf`);
      flash("📄 Line listing exported as PDF.");
    }
  };

  /* ================= LOGIN SCREEN ================= */
  if (!user) return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col items-center justify-center px-4 py-12 font-sans text-slate-800 overflow-y-auto">
      <div className="relative z-10 w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="bg-white/95 backdrop-blur-xl p-8 md:p-10 rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] border border-white/20">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4 drop-shadow-md">🛡️</div>
            <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-900 to-blue-800 tracking-tight">SkyVigilance</h2>
            <p className="text-sm font-bold text-slate-400 mt-2 uppercase tracking-widest">SafetyDB Platform · MedDRA 29.1</p>
          </div>
          <input
            placeholder="Username (e.g. anand_triage)"
            className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 w-full mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white transition-all font-medium"
            value={login.username}
            onChange={e => setLogin({ ...login, username: e.target.value })}
            onKeyDown={e => e.key === "Enter" && doLogin()}
          />
          <input
            type="password"
            placeholder="Password"
            className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 w-full mb-8 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white transition-all font-medium"
            value={login.password}
            onChange={e => setLogin({ ...login, password: e.target.value })}
            onKeyDown={e => e.key === "Enter" && doLogin()}
          />
          <button
            onClick={doLogin}
            disabled={loginLoading}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 text-white w-full py-4 rounded-xl font-bold shadow-md shadow-indigo-200 hover:shadow-lg transition-all duration-200"
          >
            {loginLoading ? "Signing In…" : "Sign In"}
          </button>
          <p className="text-xs text-slate-400 font-medium text-center mt-6">Format: basename_role (e.g. anand_medical, demo_triage)</p>
        </div>

        <div className="bg-white/10 backdrop-blur-md p-8 md:p-10 rounded-3xl shadow-2xl border border-white/10 flex flex-col h-full justify-center text-white">
          <h3 className="text-2xl font-extrabold mb-6 flex items-center gap-3">📚 Platform Resources</h3>
          <div className="space-y-8">
            <a
              href="https://drive.google.com/file/d/1_9px39tpjrhRmyXNSFYUCtFubo8crTna/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-4 bg-white/20 hover:bg-white/30 border border-white/30 text-sm font-bold rounded-xl transition-colors shadow-sm"
            >
              📄 View SafetyDB Manual V2
            </a>
            <div className="flex flex-col gap-3">
              <div className="w-full aspect-video rounded-xl overflow-hidden shadow-2xl border border-white/20 bg-slate-900">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/B4pq2AJKXPU?rel=0"
                  title="SafetyDB Demo Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="relative z-10 mt-12 text-xs font-bold text-slate-400/60 uppercase tracking-widest">A VigiServe Foundation Initiative</p>
    </div>
  );

  const chartData = STAGES.map(s => ({ name: s.name, value: cases.filter(c => c.currentStep === s.step).length }));

  const ACTION_META = {
    CASE_CREATED:        { color: "bg-blue-100 text-blue-800",     icon: "📥", label: "Case Created" },
    CASE_UPDATED:        { color: "bg-indigo-100 text-indigo-800", icon: "✏️", label: "Case Updated" },
    CASE_SUBMITTED:      { color: "bg-green-100 text-green-800",   icon: "✅", label: "Case Routed" },
    CASE_LOCKED:         { color: "bg-slate-800 text-white",       icon: "🔒", label: "Case Globally Locked" },
    CASE_UNLOCKED:       { color: "bg-amber-100 text-amber-900",   icon: "🔓", label: "Case Unlocked" },
    FOLLOWUP_ADDED:      { color: "bg-purple-100 text-purple-800", icon: "🔄", label: "Follow-Up Added" },
    MEDDRA_RECODED:      { color: "bg-purple-200 text-purple-950", icon: "🧬", label: "MedDRA 29.1 Recoded" },
    CASE_RETURNED:       { color: "bg-amber-100 text-amber-800",   icon: "↩️", label: "Returned for Query" },
    ROUTE_BACK_TO_DE:    { color: "bg-amber-100 text-amber-800",   icon: "↩️", label: "Returned to Data Entry" },
    RETURNED_TO_MEDICAL: { color: "bg-amber-100 text-amber-800",   icon: "↩️", label: "Returned to Medical" },
    APPROVED:            { color: "bg-green-100 text-green-800",   icon: "✅", label: "QC Approved" },
    E2B_EXPORTED:        { color: "bg-violet-100 text-violet-800", icon: "📨", label: "E2B XML Exported" },
    TAB_SAVED:           { color: "bg-teal-100 text-teal-800",     icon: "💾", label: "Section Saved" },
  };

  /* ================= RENDER SECTIONS ================= */
  const renderTriageForm = () => (
    <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white p-6 mb-6">
      <h3 className="text-sm font-extrabold text-slate-700 uppercase tracking-widest mb-5">📥 New Case — Triage / Initial Case Book-in</h3>

      {dupResults !== null && (
        <div className={`rounded-xl border px-4 py-3 mb-4 text-xs shadow-sm ${dupResults.length > 0 ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
          {dupResults.length > 0 ? (
            <>
              <div className="font-extrabold text-amber-800 mb-2">⚠️ Smart Duplicate Search: {dupResults.length} potential match{dupResults.length > 1 ? "es" : ""} found</div>
              {dupResults.slice(0, 3).map((d, i) => (
                <div key={i} className={`flex items-center gap-2 mb-1.5 px-3 py-2 rounded-lg shadow-sm ${d.band === "HIGH" ? "bg-red-100 text-red-800" : d.band === "MEDIUM" ? "bg-orange-100 text-orange-800" : "bg-yellow-100 text-yellow-800"}`}>
                  <span className="font-bold">{d.band}</span>
                  <span className="font-mono font-semibold">{d.caseNumber}</span>
                  <button
                    type="button"
                    onClick={() => setScoreModalDup(d)}
                    className="underline font-extrabold hover:text-indigo-900"
                  >
                    {d.score}% match (Explain)
                  </button>
                  <span className="text-slate-600">· {d.drug} · {d.event}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-slate-500 font-medium">{d.country} · {d.status}</span>
                    <button
                      type="button"
                      onClick={() => acceptDuplicateAsFollowUp(d.id, d.caseNumber)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded-md font-bold"
                    >
                      + Accept as Follow-Up
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setDupResults(null)} className="mt-2 text-amber-700 font-bold hover:underline">Dismiss</button>
            </>
          ) : (
            <span className="text-green-700 font-bold">✅ No duplicates found — safe to Book-in.</span>
          )}
        </div>
      )}

      <SectionHead>Report Details</SectionHead>
      <div className="grid grid-cols-3 gap-4">
        {F("Initial Receipt Date *", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={form.triage?.receiptDate || ""} onChange={e => setNested("triage", "receiptDate", e.target.value)} />, true)}
        {F("Country of Incidence *", S(COUNTRIES, { value: form.triage?.country || "", onChange: e => setNested("triage", "country", e.target.value) }), true)}
        {F("Report Type", S(["Spontaneous", "Literature", "Regulatory", "Clinical Trial", "Other"], { value: form.general?.reportType || "", onChange: e => setNested("general", "reportType", e.target.value) }))}
      </div>
      <div className="grid grid-cols-3 gap-4">
        {F("Patient Initials (or NF)", I({
          placeholder: "e.g. J.D.",
          value: form.triage?.patientInitials || "",
          onChange: e => setNested("triage", "patientInitials", e.target.value),
          onNfChange: code => setNested("triage", "patientInitials", code)
        }))}
        {F("Age at Onset", <input type="number" min="0" max="120" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" placeholder="Years" value={form.triage?.age || ""} onChange={e => setNested("triage", "age", e.target.value)} />)}
        {F("Sex", S(["Male", "Female", "Unknown"], { value: form.triage?.sex || "", onChange: e => setNested("triage", "sex", e.target.value) }))}
      </div>

      <SectionHead>Suspect Drug &amp; Adverse Event</SectionHead>
      <div className="grid grid-cols-3 gap-4">
        {F("Drug Name *", I({
          placeholder: "Brand or generic name",
          value: (form.products || [])[0]?.name || "",
          onChange: e => {
            const p = [...((form.products?.length) ? form.products : [{}])];
            p[0] = { ...p[0], name: e.target.value };
            setForm(f => ({ ...f, products: p }));
          }
        }), true)}
        {F("Event as Reported *", I({
          placeholder: "Exact verbatim words",
          value: (form.events || [])[0]?.term || "",
          onChange: e => {
            const ev = [...((form.events?.length) ? form.events : [{}])];
            ev[0] = { ...ev[0], term: e.target.value };
            setForm(f => ({ ...f, events: ev }));
          }
        }), true)}
        {F("Onset Date", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={(form.events || [])[0]?.onsetDate || ""} onChange={e => {
          const ev = [...((form.events?.length) ? form.events : [{}])];
          ev[0] = { ...ev[0], onsetDate: e.target.value };
          setForm(f => ({ ...f, events: ev }));
        }} />)}
      </div>

      <SectionHead>Reporter &amp; Seriousness at Intake</SectionHead>
      <div className="grid grid-cols-3 gap-4 mb-3">
        {F("Reporter Qualification", S(["Physician", "Pharmacist", "Nurse", "Patient", "Lawyer", "Other health professional", "Consumer"], { value: form.triage?.qualification || "", onChange: e => setNested("triage", "qualification", e.target.value) }))}
        {F("Reported Causality", S(["Related", "Possibly Related", "Unlikely Related", "Not Related", "Unknown"], { value: form.triage?.reportedCausality || "", onChange: e => setNested("triage", "reportedCausality", e.target.value) }))}
        {F("Reference / FAERS ID", I({ placeholder: "Optional priority match ID", value: form.triage?.faersId || "", onChange: e => setNested("triage", "faersId", e.target.value) }))}
      </div>
      <div className="flex gap-6 flex-wrap bg-red-50/60 border border-red-100 rounded-xl px-4 py-2.5">
        {[["Death", "death"], ["Life-threatening", "lifeThreatening"], ["Hospitalisation", "hospitalisation"], ["Disability", "disability"], ["Congenital anomaly", "congenital"], ["Medically significant", "medSignificant"]].map(([lbl, key]) => (
          <React.Fragment key={key}>
            {C(lbl, form.general?.seriousness?.[key], e => setForm(f => ({ ...f, general: { ...f.general, seriousness: { ...(f.general?.seriousness || {}), [key]: e.target.checked } } })), "red")}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-6 flex gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => detectDuplicate(true)}
          disabled={dupLoading}
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 text-white text-sm px-5 py-2.5 rounded-xl font-bold shadow-md flex items-center gap-2"
        >
          {dupLoading ? "⏳ Checking…" : "🔍 Smart Duplicate Search"}
        </button>
        <button
          type="button"
          onClick={createCase}
          className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-sm px-6 py-2.5 rounded-xl font-bold shadow-md flex items-center gap-2"
        >
          📥 Book-in Case
        </button>
      </div>
    </div>
  );

  const renderDataEntryForm = () => {
    const DATA_TABS = [
      { id: "general",  label: "General & Follow-Ups" },
      { id: "patient",  label: "Patient" },
      { id: "products", label: "Products" },
      { id: "events",   label: "Events + Narrative" },
    ];
    const setP = (idx, key, val2) => {
      // Use the latest state inside the updater so date edits cannot be lost to a stale closure.
      setForm(f => {
        const arr = [...((f.products?.length) ? f.products : [{}])];
        const updated = { ...arr[idx], [key]: val2 };
        if (key === "ongoing" && val2 === true) {
          updated.stopDate = "";
          updated.duration = "";
        }
        if ((key === "startDate" || key === "stopDate") && updated.startDate && updated.stopDate) {
          updated.duration = calcDaysDiff(updated.startDate, updated.stopDate);
        }
        arr[idx] = updated;
        return { ...f, products: arr };
      });
    };

    return (
      <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white p-6 mb-6">
        <div className="flex gap-2 mb-6 border-b border-slate-200 pb-0">
          {DATA_TABS.map(t2 => (
            <button
              key={t2.id}
              type="button"
              onClick={() => setTab(t2.id)}
              className={`text-sm px-5 py-2.5 rounded-t-xl font-bold transition-all ${tab === t2.id ? "bg-white border-x border-t border-slate-200 text-indigo-700 -mb-px" : "text-slate-500 hover:text-slate-800"}`}
            >
              {t2.label}
            </button>
          ))}
        </div>

        {tab === "general" && (
          <div>
            <SectionHead>Report Identifiers &amp; Aware Date</SectionHead>
            <div className="grid grid-cols-4 gap-4">
              {F("Report Type", S(["Spontaneous", "Literature", "Regulatory", "Clinical Trial", "Other"], { value: form.general?.reportType || "", onChange: e => setNested("general", "reportType", e.target.value) }))}
              {F("Initial Receipt Date", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={form.triage?.receiptDate || ""} onChange={e => setNested("triage", "receiptDate", e.target.value)} />)}
              {F("Current Aware Date", <input type="date" className="bg-indigo-50/50 border border-indigo-200 rounded-lg px-3 py-2 text-sm font-bold text-indigo-900 w-full" value={form.general?.awareDate || form.triage?.receiptDate || ""} onChange={e => setNested("general", "awareDate", e.target.value)} />)}
              {F("Medically Confirmed?", S(["Yes", "No", "Unknown"], { value: form.general?.medicallyConfirmed || "", onChange: e => setNested("general", "medicallyConfirmed", e.target.value) }))}
            </div>
            <div className="grid grid-cols-3 gap-4">
              {F("Worldwide Case Number", I({ placeholder: "HQ-2026-0001", value: form.general?.wwCaseNum || "", onChange: e => setNested("general", "wwCaseNum", e.target.value) }))}
              {F("Source of Information", S(["Healthcare professional", "Patient/Consumer", "Regulatory authority", "Literature", "Other"], { value: form.general?.source || "", onChange: e => setNested("general", "source", e.target.value) }))}
              {F("Literature Reference", I({ placeholder: "Author, journal, year…", value: form.general?.literature || "", onChange: e => setNested("general", "literature", e.target.value) }))}
            </div>

            <SectionHead>Amendments &amp; Follow-Ups (DB Versioning)</SectionHead>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
              {(form.followUps || []).length === 0 ? (
                <div className="text-xs text-slate-400 font-medium">No follow-up revisions recorded yet (Initial Version).</div>
              ) : (
                <table className="w-full text-xs mb-3">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="pb-2">Serial #</th>
                      <th className="pb-2">Designation</th>
                      <th className="pb-2">Follow-up Received</th>
                      <th className="pb-2">Justification</th>
                      <th className="pb-2">Added By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(form.followUps || []).map((fu, idx) => (
                      <tr key={idx}>
                        <td className="py-2 font-mono font-bold">#{fu.serial || idx + 1}</td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded font-bold ${fu.amendment ? "bg-blue-100 text-blue-800" : fu.significant ? "bg-red-100 text-red-800" : "bg-slate-200 text-slate-700"}`}>
                            {fu.amendment ? "(A) Amendment" : fu.significant ? "(S) Significant F/U" : "(NS) Non-Significant F/U"}
                          </span>
                        </td>
                        <td className="py-2 font-medium">{fu.receivedDate}</td>
                        <td className="py-2 text-slate-600">{fu.justification}</td>
                        <td className="py-2 text-slate-500">{fu.addedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <button
                type="button"
                onClick={async () => {
                  const isSig = window.confirm("Is this follow-up SIGNIFICANT? (Click OK for Significant (S) F/U, Cancel for Non-Significant (NS) F/U)");
                  const just = window.prompt("Enter Follow-Up / Amendment Justification:");
                  if (!just) return;
                  await axios.post(`${API}/cases/${selected.id}/followup`, {
                    performedBy: user.username, role: user.role,
                    receivedDate: new Date().toISOString().slice(0, 10),
                    significant: isSig, amendment: false, justification: just
                  }).then(res => {
                    setSelected(res.data); setForm(res.data); fetchCases(); fetchAudit(selected.id);
                    flash(`🔄 Follow-up added (${isSig ? "Significant" : "Non-significant"}).`);
                  });
                }}
                className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-900 font-bold px-3 py-1.5 rounded-lg"
              >
                + Add Follow-Up Entry
              </button>
            </div>

            <SectionHead>Reporter Information</SectionHead>
            <div className="grid grid-cols-2 gap-4">
              {F("First Name (or NF)", I({
                value: form.general?.reporter?.firstName || "",
                onChange: e => setDeep("general", "reporter", "firstName", e.target.value),
                onNfChange: c => setDeep("general", "reporter", "firstName", c)
              }))}
              {F("Last Name (or NF)", I({
                value: form.general?.reporter?.lastName || "",
                onChange: e => setDeep("general", "reporter", "lastName", e.target.value),
                onNfChange: c => setDeep("general", "reporter", "lastName", c)
              }))}
            </div>
            <div className="grid grid-cols-3 gap-4">
              {F("Institution", I({ value: form.general?.reporter?.institution || "", onChange: e => setDeep("general", "reporter", "institution", e.target.value) }))}
              {F("Phone", I({ value: form.general?.reporter?.phone || "", onChange: e => setDeep("general", "reporter", "phone", e.target.value) }))}
              {F("Email", I({ value: form.general?.reporter?.email || "", onChange: e => setDeep("general", "reporter", "email", e.target.value) }))}
            </div>
            {C("Reporter also patient?", form.general?.reporterIsPatient, e => setNested("general", "reporterIsPatient", e.target.checked))}
            {C("Protect Reporter Confidentiality (Withhold Name & Address / MSK)?", form.general?.reporterAnonymous, e => setNested("general", "reporterAnonymous", e.target.checked))}

            <SectionHead>Seriousness Classification</SectionHead>
            <div className="bg-gradient-to-r from-red-50 to-white border border-red-100 rounded-2xl p-5 mb-4 shadow-sm">
              <div className="grid grid-cols-2 gap-3">
                {[["Death", "death"], ["Life-threatening", "lifeThreatening"], ["Hospitalisation", "hospitalisation"], ["Disability/Incapacity", "disability"], ["Congenital anomaly", "congenital"], ["Medically significant", "medSignificant"]].map(([l2, k]) => (
                  <React.Fragment key={k}>
                    {C(l2, (form.general?.seriousness || {})[k], e => setForm(f => ({ ...f, general: { ...f.general, seriousness: { ...(f.general?.seriousness || {}), [k]: e.target.checked } } })), "red")}
                  </React.Fragment>
                ))}
              </div>
              {autoSerious() && (
                <div className="mt-4 bg-red-100 border border-red-300 text-red-800 text-sm px-4 py-3 rounded-xl font-bold">
                  ⚠️ Case classified as SERIOUS ({getUnifiedSeriousness(form).isFatalOrLT ? "7-Day Fatal/LT Clock" : "15-Day Expedited Clock"}).
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button type="button" onClick={() => saveTab({ general: form.general, triage: form.triage }, "General")} className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm px-6 py-2.5 rounded-xl font-bold shadow-md">
                💾 Save General
              </button>
            </div>
          </div>
        )}

        {tab === "patient" && (
          <div>
            <div className="flex justify-between items-center">
              <SectionHead>Demographics &amp; Confidentiality</SectionHead>
              <button type="button" onClick={copyPatientFromReporter} className="text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold px-3 py-1.5 rounded-lg border border-indigo-200">
                📋 Patient Info From Reporter
              </button>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {F("Patient Initials (or NF)", I({
                value: form.patient?.initials || form.triage?.patientInitials || "",
                onChange: e => setNested("patient", "initials", e.target.value),
                onNfChange: c => setNested("patient", "initials", c)
              }))}
              {F("Date of Birth", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={form.patient?.dob || ""} onChange={e => setNested("patient", "dob", e.target.value)} />)}
              {F("Age at Onset", <input type="number" min="0" max="120" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={form.patient?.age || ""} onChange={e => setNested("patient", "age", e.target.value)} />)}
              {F("Age Unit", S(["Years", "Months", "Weeks", "Days", "Hours"], { value: form.patient?.ageUnit || "Years", onChange: e => setNested("patient", "ageUnit", e.target.value) }))}
            </div>
            <div className="grid grid-cols-3 gap-4">
              {F("Sex", S(["Male", "Female", "Unknown"], { value: form.patient?.sex || "", onChange: e => setNested("patient", "sex", e.target.value) }))}
              {F("Weight (kg)", <input type="number" min="0" step="0.1" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={form.patient?.weight || ""} onChange={e => setNested("patient", "weight", e.target.value)} />)}
              {F("Height (cm)", <input type="number" min="0" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={form.patient?.height || ""} onChange={e => setNested("patient", "height", e.target.value)} />)}
            </div>
            {C("Protect Patient Confidentiality (Print 'PRIVACY' & MSK on reports)?", form.patient?.protectConfidentiality, e => setNested("patient", "protectConfidentiality", e.target.checked))}

            <SectionHead>Medical History &amp; Lab Data</SectionHead>
            {F("Medical History / Additional Notes (legacy free text)", TA({ value: form.patient?.medHistory || "", onChange: e => setNested("patient", "medHistory", e.target.value) }))}
            <div className="mt-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="text-xs font-extrabold text-indigo-700 uppercase tracking-widest">Medical History Records (multiple entries; code each independently)</div>
                <button type="button" onClick={() => setForm(f => ({ ...f, patient: { ...(f.patient || {}), otherHistory: [...(f.patient?.otherHistory || []), {}] } }))} className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-lg shadow-sm border border-indigo-700">＋ Add Medical History</button>
              </div>
              {(form.patient?.otherHistory || []).map((history, idx) => (
                <div key={idx} className="border border-indigo-100 rounded-2xl p-4 mb-4 bg-indigo-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-bold text-slate-700">Medical History {idx + 1}</div>
                    <button type="button" onClick={() => setForm(f => ({ ...f, patient: { ...f.patient, otherHistory: (f.patient?.otherHistory || []).filter((_, i) => i !== idx) } }))} className="text-rose-600 text-xs font-bold" aria-label={`Remove medical history ${idx + 1}`}>Remove</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    {F("Condition / History Description", I({ value: history.description || "", onChange: e => { const a = [...(form.patient?.otherHistory || [])]; a[idx] = { ...a[idx], description: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, otherHistory: a } })); } }))}
                    {F("Duration (e.g. 5 years)", I({ value: history.duration || "", onChange: e => { const a = [...(form.patient?.otherHistory || [])]; a[idx] = { ...a[idx], duration: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, otherHistory: a } })); } }))}
                    {F("Onset / Start Date", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={history.startDate || ""} onChange={e => { const a = [...(form.patient?.otherHistory || [])]; a[idx] = { ...a[idx], startDate: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, otherHistory: a } })); }} />)}
                  </div>
                  <MedDRAWidget
                    targetSection="history" targetIdx={idx}
                    currentPt={history.meddraPt} currentPtCode={history.meddraPtCode}
                    currentLlt={history.meddraLlt} currentSoc={history.meddraSoc}
                    meddraQuery={meddraQuery} meddraResults={meddraResults}
                    meddraLoading={meddraLoading} meddraTarget={meddraTarget}
                    setMeddraTarget={setMeddraTarget} searchMeddra={searchMeddra} pickMeddra={pickMeddra}
                    setMeddraQuery={setMeddraQuery} setMeddraResults={setMeddraResults}
                    includeNonCurrent={includeNonCurrent} setIncludeNonCurrent={setIncludeNonCurrent}
                    onClear={() => { const a = [...(form.patient?.otherHistory || [])]; a[idx] = { ...a[idx], meddraPt: "", meddraPtCode: "", meddraLlt: "", meddraHlt: "", meddraSoc: "" }; setForm(f => ({ ...f, patient: { ...f.patient, otherHistory: a } })); }}
                  />
                </div>
              ))}
            </div>
            {F("Concomitant Medications", TA({ value: form.patient?.concomitant || "", onChange: e => setNested("patient", "concomitant", e.target.value) }))}

            <div className="mt-5">
              <div className="text-xs font-extrabold text-teal-700 uppercase tracking-widest mb-3">Lab / Test Results</div>
              {(form.patient?.labData || [{}]).map((lab, idx) => (
                <div key={idx} className="border border-slate-200 rounded-2xl p-4 mb-4 bg-slate-50">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                    {F("Test Name", I({ value: lab.testName || "", onChange: e => { const a = [...(form.patient?.labData || [{}])]; a[idx] = { ...a[idx], testName: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, labData: a } })); } }))}
                    {F("Result", I({ value: lab.result || "", onChange: e => { const a = [...(form.patient?.labData || [{}])]; a[idx] = { ...a[idx], result: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, labData: a } })); } }))}
                    {F("Units", I({ value: lab.units || "", onChange: e => { const a = [...(form.patient?.labData || [{}])]; a[idx] = { ...a[idx], units: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, labData: a } })); } }))}
                    {F("Reference Range", I({ value: lab.referenceRange || "", onChange: e => { const a = [...(form.patient?.labData || [{}])]; a[idx] = { ...a[idx], referenceRange: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, labData: a } })); } }))}
                    {F("Collection Date", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={lab.date || ""} onChange={e => { const a = [...(form.patient?.labData || [{}])]; a[idx] = { ...a[idx], date: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, labData: a } })); }} />)}
                    {F("Abnormality", S(["Not assessed", "Normal", "Abnormal - High", "Abnormal - Low", "Clinically significant", "Unknown"], { value: lab.abnormality || "Not assessed", onChange: e => { const a = [...(form.patient?.labData || [{}])]; a[idx] = { ...a[idx], abnormality: e.target.value }; setForm(f => ({ ...f, patient: { ...f.patient, labData: a } })); } }))}
                  </div>
                  <div className="flex justify-end mb-2"><button type="button" onClick={() => setForm(f => ({ ...f, patient: { ...f.patient, labData: (f.patient?.labData || []).filter((_, i) => i !== idx) } }))} className="text-rose-600 text-xs font-bold" aria-label={`Remove lab result ${idx + 1}`}>Remove Lab Result</button></div>
                  <MedDRAWidget
                    targetSection="lab" targetIdx={idx}
                    currentPt={lab.meddraPt} currentPtCode={lab.meddraPtCode}
                    currentLlt={lab.meddraLlt} currentSoc={lab.meddraSoc}
                    meddraQuery={meddraQuery} meddraResults={meddraResults}
                    meddraLoading={meddraLoading} meddraTarget={meddraTarget}
                    setMeddraTarget={setMeddraTarget} searchMeddra={searchMeddra} pickMeddra={pickMeddra}
                    setMeddraQuery={setMeddraQuery} setMeddraResults={setMeddraResults}
                    includeNonCurrent={includeNonCurrent} setIncludeNonCurrent={setIncludeNonCurrent}
                    onClear={() => { const a = [...(form.patient?.labData || [{}])]; a[idx] = { ...a[idx], meddraPt: "", meddraPtCode: "" }; setForm(f => ({ ...f, patient: { ...f.patient, labData: a } })); }}
                  />
                </div>
              ))}
              <button type="button" onClick={() => setForm(f => ({ ...f, patient: { ...f.patient, labData: [...(f.patient?.labData || [{}]), {}] } }))} className="text-indigo-600 text-sm font-bold">+ Add Lab Result</button>
            </div>

            <div className="mt-6 flex justify-end">
              <button type="button" onClick={() => saveTab({ patient: form.patient }, "Patient")} className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm px-6 py-2.5 rounded-xl font-bold shadow-md">
                💾 Save Patient
              </button>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div>
            {((form.products?.length) ? form.products : [{}]).map((p2, i) => {
              const hideChallenge = ["Concomitant", "Past therapy"].includes(p2.role);
              return (
                <div key={i} className="border border-slate-200 rounded-2xl p-5 mb-5 bg-slate-50">
                  <div className="text-xs font-extrabold text-slate-400 uppercase mb-3 tracking-widest">Product {i + 1}</div>
                  <div className="grid grid-cols-3 gap-4">
                    {F("Product Role *", S(["Suspect", "Co-suspect", "Interacting", "Concomitant", "Past therapy"], { value: p2.role || "Suspect", onChange: e => setP(i, "role", e.target.value) }), true)}
                    {F("Brand Name *", I({ value: p2.name || "", onChange: e => setP(i, "name", e.target.value) }), true)}
                    {F("Generic Name", I({ value: p2.genericName || "", onChange: e => setP(i, "genericName", e.target.value) }))}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {F("Dose", I({ value: p2.dose || "", onChange: e => setP(i, "dose", e.target.value) }))}
                    {F("Dose Unit", S(["mg", "mcg", "g", "IU", "mL", "mg/kg", "Other"], { value: p2.doseUnit || "", onChange: e => setP(i, "doseUnit", e.target.value) }))}
                    {F("Route", S(["Oral", "Intravenous", "Intramuscular", "Subcutaneous", "Topical", "Inhalation", "Other"], { value: p2.route || "", onChange: e => setP(i, "route", e.target.value) }))}
                  </div>
                  {F("Reported Indication", I({ value: p2.indication || "", onChange: e => setP(i, "indication", e.target.value) }))}
                  <MedDRAWidget
                    targetSection="indication" targetIdx={i}
                    currentPt={p2.indicationPt} currentPtCode={p2.indicationPtCode}
                    currentLlt={p2.indicationLlt} currentSoc={p2.indicationSoc}
                    meddraQuery={meddraQuery} meddraResults={meddraResults}
                    meddraLoading={meddraLoading} meddraTarget={meddraTarget}
                    setMeddraTarget={setMeddraTarget} searchMeddra={searchMeddra} pickMeddra={pickMeddra}
                    setMeddraQuery={setMeddraQuery} setMeddraResults={setMeddraResults}
                    includeNonCurrent={includeNonCurrent} setIncludeNonCurrent={setIncludeNonCurrent}
                    onClear={() => setP(i, "indicationPt", "")}
                  />
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    {F("Start Date", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={p2.startDate || ""} onChange={e => setP(i, "startDate", e.target.value)} />)}
                    {!p2.ongoing && F("Stop Date", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={p2.stopDate || ""} onChange={e => setP(i, "stopDate", e.target.value)} />)}
                    {!p2.ongoing && F("Duration (Auto-calculated)", I({ value: p2.duration || "", onChange: e => setP(i, "duration", e.target.value) }))}
                  </div>
                  {C("Ongoing Regimen (disables Stop Date & Duration per DB rules)", p2.ongoing, e => setP(i, "ongoing", e.target.checked))}
                  {!hideChallenge && (
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      {F("Dechallenge Result", S(["Positive – Event abated on withdrawal", "Negative – Event did not abate", "Not done", "Unknown", "N/A"], { value: p2.dechallenge || "", onChange: e => setP(i, "dechallenge", e.target.value) }))}
                      {F("Rechallenge Result", S(["Positive – Event recurred", "Negative – Event did not recur", "Not done", "Unknown", "N/A"], { value: p2.rechallenge || "", onChange: e => setP(i, "rechallenge", e.target.value) }))}
                    </div>
                  )}
                </div>
              );
            })}
            <button type="button" onClick={() => setForm(f => ({ ...f, products: [...(f.products || [{}]), {}] }))} className="text-indigo-600 text-sm font-bold">+ Add Product</button>
            <div className="mt-6 flex justify-end">
              <button type="button" onClick={() => saveTab({ products: form.products }, "Products")} className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm px-6 py-2.5 rounded-xl font-bold shadow-md">
                💾 Save Products
              </button>
            </div>
          </div>
        )}

        {tab === "events" && (
          <div>
            {((form.events?.length) ? form.events : [{}]).map((e, i) => {
              const firstDose = (form.products || [])[0]?.startDate;
              const setEv = (idx, key, val2) => {
                // Use latest form state for every event field, including stopDate.
                setForm(f => {
                  const arr = [...((f.events?.length) ? f.events : [{}])];
                  const updated = { ...arr[idx], [key]: val2 };
                  const currentFirstDose = (f.products || [])[0]?.startDate;
                  if (key === "onsetDate" && currentFirstDose && val2) {
                    updated.onsetLatency = calcDaysDiff(currentFirstDose, val2);
                  }
                  if (key === "outcome" && val2 === "Fatal") {
                    updated.seriousness = { ...(updated.seriousness || {}), death: true };
                  }
                  arr[idx] = updated;
                  return { ...f, events: arr };
                });
              };
              return (
                <div key={i} className="border border-slate-200 rounded-2xl p-5 mb-5 bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-extrabold text-slate-400 uppercase tracking-widest">Event {i + 1}</div>
                    {((form.events?.length) || 0) > 1 && (
                      <button
                        type="button"
                        onClick={() => setForm(f => {
                          const arr = [...(f.events || [{}])];
                          arr.splice(i, 1);
                          return { ...f, events: arr.length ? arr : [{}] };
                        })}
                        className="text-xs font-bold text-rose-600 hover:text-rose-800 border border-rose-200 hover:border-rose-400 rounded-lg px-3 py-1.5"
                        aria-label={`Remove Event ${i + 1}`}
                      >
                        🗑 Remove Event
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {F("Description as Reported *", I({ value: e.term || "", onChange: ev => setEv(i, "term", ev.target.value) }), true)}
                    {F("Diagnosis / Symptom (D/S)", S([{ v: "D", l: "D — Diagnosis" }, { v: "S", l: "S — Symptom" }], { value: e.diagSymptom || "", onChange: ev => setEv(i, "diagSymptom", ev.target.value) }))}
                    {F("Outcome", S(["Recovered / Resolved", "Recovering / Resolving", "Not recovered / Not resolved", "Recovered with sequelae", "Fatal", "Unknown"], { value: e.outcome || "", onChange: ev => setEv(i, "outcome", ev.target.value) }))}
                  </div>
                  <MedDRAWidget
                    targetSection="event" targetIdx={i}
                    currentPt={e.pt} currentPtCode={e.pt_code}
                    currentLlt={e.llt} currentSoc={e.soc}
                    currentVersion={e.meddra_version} isNonCurrent={e.non_current}
                    meddraQuery={meddraQuery} meddraResults={meddraResults}
                    meddraLoading={meddraLoading} meddraTarget={meddraTarget}
                    setMeddraTarget={setMeddraTarget} searchMeddra={searchMeddra} pickMeddra={pickMeddra}
                    setMeddraQuery={setMeddraQuery} setMeddraResults={setMeddraResults}
                    includeNonCurrent={includeNonCurrent} setIncludeNonCurrent={setIncludeNonCurrent}
                    onClear={() => setEv(i, "pt", "")}
                  />
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    {F("Onset Date", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={e.onsetDate || ""} onChange={ev => setEv(i, "onsetDate", ev.target.value)} />)}
                    {F("Stop Date", <input type="date" className="bg-white/50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={e.stopDate || ""} onChange={ev => setEv(i, "stopDate", ev.target.value)} />)}
                    {F("Onset Latency (Onset - First Dose)", I({ placeholder: "Auto-calculated", value: e.onsetLatency || calcDaysDiff(firstDose, e.onsetDate) || "", onChange: ev => setEv(i, "onsetLatency", ev.target.value) }))}
                  </div>

                  <SectionHead>Seriousness at Event Level</SectionHead>
                  <div className="grid grid-cols-3 gap-2 bg-red-50/60 p-4 rounded-xl border border-red-100">
                    {[["Death", "death"], ["Life-threatening", "lifeThreatening"], ["Hospitalisation", "hospitalisation"], ["Disability", "disability"], ["Congenital anomaly", "congenital"], ["Medically significant", "medSignificant"]].map(([lbl5, key]) => (
                      <React.Fragment key={key}>
                        {C(lbl5, e.seriousness?.[key] || (key === "hospitalisation" && e.seriousness?.hospitalised), ev => {
                          const s = { ...(e.seriousness || {}), [key]: ev.target.checked };
                          setEv(i, "seriousness", s);
                        }, "red")}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
            <button type="button" onClick={() => setForm(f => ({ ...f, events: [...(f.events || [{}]), {}] }))} className="text-indigo-600 text-sm font-bold">+ Add Event</button>

            <div className="mt-8 border-t border-slate-200 pt-6">
              <div className="flex justify-between items-center mb-3">
                <div className="text-sm font-extrabold text-teal-700 uppercase tracking-widest">Case Narrative</div>
                <button type="button" onClick={generateNarrative} className="text-xs bg-teal-100 hover:bg-teal-200 text-teal-900 px-4 py-2 rounded-xl font-bold">
                  ⚡ Auto-generate Narrative
                </button>
              </div>
              <textarea
                className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm w-full resize-none"
                rows={8}
                placeholder="Enter or auto-generate the full case narrative…"
                value={form.narrative || ""}
                onChange={e => setForm(f => ({ ...f, narrative: e.target.value }))}
              />
              <div className="mt-5 flex justify-end">
                <button type="button" onClick={() => saveTab({ events: form.events, narrative: form.narrative }, "Events & Narrative")} className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm px-6 py-2.5 rounded-xl font-bold shadow-md">
                  💾 Save Events &amp; Narrative
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMedicalForm = () => {
    const m = form.medical || {};
    const setEv = (idx, key, val) => {
      const arr = [...(form.events || [{}])];
      arr[idx] = { ...arr[idx], [key]: val };
      setForm(f => ({ ...f, events: arr }));
    };

    return (
      <div className="space-y-6 bg-white/80 backdrop-blur-md rounded-2xl border border-white p-6 mb-6">
        <SectionHead color="purple">Events Review, MedDRA 29.1 Coding &amp; Causality</SectionHead>
        {(form.events || [{}]).map((e, idx) => (
          <div key={idx} className="bg-white border border-purple-200 rounded-xl p-4 mb-4 shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-purple-900">Event {idx + 1}: {e.term || "(No reported term)"}</span>
              <span className="text-xs font-medium text-slate-500">Onset: {e.onsetDate || "—"} · Latency: {e.onsetLatency || "—"}</span>
            </div>
            <MedDRAWidget
              targetSection="medical_event" targetIdx={idx}
              currentPt={e.pt} currentPtCode={e.pt_code}
              currentLlt={e.llt} currentSoc={e.soc}
              currentVersion={e.meddra_version} isNonCurrent={e.non_current}
              meddraQuery={meddraQuery} meddraResults={meddraResults}
              meddraLoading={meddraLoading} meddraTarget={meddraTarget}
              setMeddraTarget={setMeddraTarget} searchMeddra={searchMeddra} pickMeddra={pickMeddra}
              setMeddraQuery={setMeddraQuery} setMeddraResults={setMeddraResults}
              includeNonCurrent={includeNonCurrent} setIncludeNonCurrent={setIncludeNonCurrent}
              onClear={() => setEv(idx, "pt", "")}
            />
            <div className="grid grid-cols-3 gap-4 mt-3">
              {F("Listedness", S(["Listed", "Unlisted", "Unknown"], { value: e.listedness || "", onChange: ev => setEv(idx, "listedness", ev.target.value) }))}
              {F("Company Causality", S(["Related", "Possibly Related", "Unlikely Related", "Not Related", "Unknown"], { value: e.causalityReported || "", onChange: ev => setEv(idx, "causalityReported", ev.target.value) }))}
              {F("Causality Method", S(["WHO-UMC", "Naranjo", "CIOMS", "Other"], { value: e.causalityMethod || "", onChange: ev => setEv(idx, "causalityMethod", ev.target.value) }))}
            </div>

            {e.causalityMethod === "WHO-UMC" && (
              <div className="bg-yellow-50/70 border border-yellow-200 rounded-xl p-4 mt-2">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {C("Temporal association plausible", e.temporal, ev => setEv(idx, "temporal", ev.target.checked))}
                  {C("Dechallenge positive", e.dechallenge, ev => setEv(idx, "dechallenge", ev.target.checked))}
                  {C("Rechallenge positive", e.rechallenge, ev => setEv(idx, "rechallenge", ev.target.checked))}
                  {C("Alternative cause exists", e.alternative, ev => setEv(idx, "alternative", ev.target.checked))}
                </div>
                <div className="flex items-center gap-4">
                  <button type="button" onClick={() => runWHOUMC(idx)} className="bg-amber-500 text-white text-xs px-4 py-2 rounded-lg font-bold">⚙️ Run WHO-UMC</button>
                  {e.causality && <span className="text-xs font-extrabold text-yellow-900">Result: {e.causality}</span>}
                </div>
              </div>
            )}

            {e.causalityMethod === "Naranjo" && (
              <div className="bg-orange-50/70 border border-orange-200 rounded-xl p-4 mt-2">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {C("Previous conclusive reports (+1)", e.nar_previous, ev => setEv(idx, "nar_previous", ev.target.checked))}
                  {C("ADE appeared after suspect drug (+2)", e.nar_reaction, ev => setEv(idx, "nar_reaction", ev.target.checked))}
                  {C("Improved on withdrawal (+1)", e.nar_dechallenge, ev => setEv(idx, "nar_dechallenge", ev.target.checked))}
                  {C("Reappeared on readministration (+2)", e.nar_rechallenge, ev => setEv(idx, "nar_rechallenge", ev.target.checked))}
                  {C("Alternative causes exist (-1)", e.nar_alternative, ev => setEv(idx, "nar_alternative", ev.target.checked))}
                  {C("Confirmed by objective evidence (+1)", e.nar_confirmed, ev => setEv(idx, "nar_confirmed", ev.target.checked))}
                </div>
                <div className="flex items-center gap-4">
                  <button type="button" onClick={() => runNaranjo(idx)} className="bg-orange-500 text-white text-xs px-4 py-2 rounded-lg font-bold">⚙️ Calculate Naranjo</button>
                  {e.naranjScore !== undefined && <span className="text-xs font-extrabold text-orange-900">Score: {e.naranjScore} → {e.naranjResult}</span>}
                </div>
              </div>
            )}
          </div>
        ))}

        <SectionHead color="purple">Case Analysis &amp; Narrative</SectionHead>
        {F("Medical Reviewer Comments", TA({ rows: 3, value: m.comments || "", onChange: e => setNested("medical", "comments", e.target.value) }))}
        <textarea className="bg-white border border-purple-200 rounded-xl px-4 py-3 text-sm w-full" rows={6} value={form.narrative || ""} onChange={e => setForm(f => ({ ...f, narrative: e.target.value }))} />
        <div className="flex justify-between items-center pt-3">
          <button type="button" onClick={returnCaseToDataEntry} className="bg-amber-500 text-white text-sm px-5 py-2.5 rounded-xl font-bold">
            ↩️ Return to Data Entry
          </button>
          <button type="button" onClick={() => saveTab({ medical: form.medical, events: form.events, narrative: form.narrative }, "Medical Review")} className="bg-purple-600 text-white text-sm px-6 py-2.5 rounded-xl font-bold">
            💾 Save Medical Review
          </button>
        </div>
      </div>
    );
  };

  const renderQualityForm = () => {
    const q = form.quality || {}, p = form.patient || {};
    const allDrugs = (form.products || []).map(d => `${d.name || "?"}${d.dose ? " (" + d.dose + ")" : ""}`).join("; ");
    const allPts = (form.events || []).map(e => `${e.pt || e.term}${e.non_current ? " * (Non-Current)" : ""}`).filter(Boolean).join("; ");
    const qcItems = [
      ["patientComplete", "Patient demographics complete?"],
      ["drugComplete", "Suspect drug information complete?"],
      ["eventCoded", "Adverse event(s) MedDRA 29.1 coded (and no non-current * terms)?"],
      ["narrativeComplete", "Case narrative present and adequate?"],
      ["causalityAssessed", "Causality assessed by medical reviewer?"],
      ["seriousnessCorrect", "Seriousness classification correct?"],
      ["listednessSet", "Listedness assessed?"],
      ["duplicateChecked", "Duplicate search performed?"],
    ];
    return (
      <div className="space-y-6 bg-white/80 rounded-2xl border border-white p-6 mb-6">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-sm grid grid-cols-2 gap-y-2">
          <span className="text-slate-500">Case Number:</span><span className="font-bold">{selected.caseNumber}</span>
          <span className="text-slate-500">Patient:</span><span className="font-bold">{p.age ? `${p.age} ${p.ageUnit || "yrs"} ${p.sex || ""}` : "Not entered"}</span>
          <span className="text-slate-500">Suspect Product(s):</span><span className="font-bold">{allDrugs || "—"}</span>
          <span className="text-slate-500">MedDRA 29.1 PT(s):</span><span className="font-bold text-purple-700">{allPts || "Not coded"}</span>
          <span className="text-slate-500">Seriousness:</span><span className={`font-extrabold ${autoSerious() ? "text-red-600" : "text-green-600"}`}>{autoSerious() ? "🔴 Serious" : "🟢 Non-serious"}</span>
        </div>

        <SectionHead color="orange">Quality Control Checklist</SectionHead>
        <div className="bg-orange-50/60 border border-orange-200 rounded-2xl p-5 space-y-2">
          {qcItems.map(([key, lbl6]) => (
            <React.Fragment key={key}>
              {C(lbl6, q[key], e => setForm(f => ({ ...f, quality: { ...f.quality, [key]: e.target.checked } })), q[key] ? "green" : "orange")}
            </React.Fragment>
          ))}
        </div>
        {F("Quality Review Comments", TA({ rows: 3, value: q.comments || "", onChange: e => setForm(f => ({ ...f, quality: { ...f.quality, comments: e.target.value } })) }))}
        <div className="flex gap-4">
          <button type="button" onClick={() => setForm(f => ({ ...f, quality: { ...f.quality, finalStatus: "approved" } }))} className={`bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold ${q.finalStatus === "approved" ? "ring-4 ring-green-300" : ""}`}>✅ Approve Case</button>
          <button type="button" onClick={() => setForm(f => ({ ...f, quality: { ...f.quality, finalStatus: "returned" } }))} className={`bg-orange-500 text-white px-5 py-2.5 rounded-xl text-sm font-bold ${q.finalStatus === "returned" ? "ring-4 ring-orange-300" : ""}`}>↩️ Return to Medical</button>
        </div>
      </div>
    );
  };

  const renderSubmissionsForm = () => {
    const sub = form.submissions || {};
    const selectedAgencies = sub.agencies || {};
    const toggleAgency = (id) => {
      setForm(f => ({
        ...f,
        submissions: {
          ...f.submissions,
          agencies: {
            ...(f.submissions?.agencies || {}),
            [id]: { ...(f.submissions?.agencies?.[id] || {}), selected: !(f.submissions?.agencies?.[id]?.selected) }
          }
        }
      }));
    };
    const setAgencyField = (id, key, val2) => {
      setForm(f => ({
        ...f,
        submissions: {
          ...f.submissions,
          agencies: {
            ...(f.submissions?.agencies || {}),
            [id]: { ...(f.submissions?.agencies?.[id] || {}), [key]: val2 }
          }
        }
      }));
    };

    return (
      <div className="space-y-4 bg-white/80 rounded-2xl border border-white p-6 mb-6">
        <SectionHead color="violet">Expedited Regulatory Submissions</SectionHead>
        {AGENCIES.map(agency => {
          const ag = selectedAgencies[agency.id] || {};
          return (
            <div key={agency.id} className={`border rounded-2xl p-4 ${ag.selected ? "border-violet-300 bg-violet-50/40" : "border-slate-200 bg-white"}`}>
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => toggleAgency(agency.id)}>
                <input type="checkbox" checked={!!ag.selected} readOnly className="w-4 h-4 accent-violet-600" />
                <span className="font-extrabold text-sm">{agency.label}</span>
                <span className="text-xs text-slate-400">{agency.country}</span>
                {ag.submittedDate && <span className="ml-auto text-xs bg-green-100 text-green-800 px-2.5 py-0.5 rounded-full font-bold">✅ Submitted {ag.submittedDate}</span>}
              </div>
              {ag.selected && (
                <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-slate-100">
                  {F("Submission Date", <input type="date" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm w-full" value={ag.submittedDate || ""} onChange={e => setAgencyField(agency.id, "submittedDate", e.target.value)} />)}
                  {F("Method", S(["E2B(R3) XML — Electronic", "Agency Portal", "Paper / CIOMS I"], { value: ag.method || "", onChange: e => setAgencyField(agency.id, "method", e.target.value) }))}
                  {F("ACK / Tracking Ref", I({ value: ag.refNumber || "", onChange: e => setAgencyField(agency.id, "refNumber", e.target.value) }))}
                </div>
              )}
            </div>
          );
        })}
        <div className="flex justify-end pt-3">
          <button type="button" onClick={() => saveTab({ submissions: form.submissions }, "Submissions")} className="bg-violet-600 text-white text-sm px-6 py-2.5 rounded-xl font-bold">
            💾 Save Submissions
          </button>
        </div>
      </div>
    );
  };

  const renderArchivalForm = () => {
    const arc = form.archival || {};
    return (
      <div className="space-y-4 bg-white/80 rounded-2xl border border-white p-6 mb-6">
        <SectionHead color="indigo">Formal Case Closure &amp; Archival</SectionHead>
        <div className="grid grid-cols-2 gap-4">
          {F("Archival Date", <input type="date" className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" value={arc.archivalDate || new Date().toISOString().slice(0, 10)} onChange={e => setNested("archival", "archivalDate", e.target.value)} />)}
          {F("Retention Period", S(["5 years", "10 years", "Lifetime of product + 10 years"], { value: arc.retention || "", onChange: e => setNested("archival", "retention", e.target.value) }))}
        </div>
        {F("Archival Notes", TA({ rows: 3, value: arc.notes || "", onChange: e => setNested("archival", "notes", e.target.value) }))}
        <div className="flex justify-end">
          <button type="button" onClick={() => saveTab({ archival: form.archival }, "Archival")} className="bg-slate-800 text-white text-sm px-6 py-2.5 rounded-xl font-bold">
            🗄️ Save Archival Details
          </button>
        </div>
      </div>
    );
  };

  const renderReadOnlySummary = () => {
    const ser = getUnifiedSeriousness(form);
    return (
      <div className="space-y-4 bg-white/80 rounded-2xl border border-white p-6 mb-6">
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-xl font-bold">
          👁️ Read-only — Case is at <strong>{selected.status}</strong> (Step {selected.currentStep}). Your role: <strong>{user.role}</strong> (Step {user.step}).
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-sm space-y-2.5">
          {[
            ["Status", `${selected.status} ${selected.locked ? "(🔒 Locked)" : "(🔓 Unlocked)"}`],
            ["Aware Date", form.general?.awareDate || form.triage?.receiptDate],
            ["Country", form.triage?.country],
            ["Patient", form.patient?.age ? `${form.patient.age} ${form.patient.ageUnit || "yrs"} · ${form.patient.sex || ""}` : "Not entered"],
            ["Suspect Product(s)", (form.products || []).map(p => p.name).filter(Boolean).join("; ") || "Not entered"],
            ["Event PT(s) (MedDRA 29.1)", (form.events || []).map(e => `${e.pt || e.term}${e.non_current ? " *" : ""}`).filter(Boolean).join("; ") || "Not coded"],
            ["Seriousness", ser.isSerious ? `Yes — Serious (${ser.fLtTag})` : "No (Non-serious)"],
            ["Causality", (form.events || []).map(e => e.causality).filter(Boolean).join("; ") || "Pending"],
            ["Listedness", (form.events || []).map(e => e.listedness).filter(Boolean).join("; ") || "Pending"],
          ].map(([k, v]) => (
            <div key={k} className="flex gap-3">
              <span className="text-slate-500 w-44 flex-shrink-0 font-bold">{k}:</span>
              <span className="font-medium">{v || "—"}</span>
            </div>
          ))}
          {form.narrative && <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4 text-sm leading-relaxed">{form.narrative}</div>}
        </div>
      </div>
    );
  };

  const renderModalForm = () => {
    if (!selected) return null;
    if (selected.currentStep > 6 || !isMyCase(selected)) return renderReadOnlySummary();
    if (selected.currentStep === 2) return renderDataEntryForm();
    if (selected.currentStep === 3) return renderMedicalForm();
    if (selected.currentStep === 4) return renderQualityForm();
    if (selected.currentStep === 5) return renderSubmissionsForm();
    if (selected.currentStep === 6) return renderArchivalForm();
    return renderReadOnlySummary();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 font-sans flex flex-col text-slate-800">
      {/* Top Navbar */}
      <div className="sticky top-0 z-30 bg-white/70 backdrop-blur-lg border-b border-white/50 px-6 py-4 flex justify-between items-center shadow-sm">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-indigo-500 to-blue-600 p-2 rounded-xl shadow-sm">
            <span className="text-xl text-white">🛡️</span>
          </div>
          <span className="font-extrabold text-indigo-900 text-lg tracking-tight">SkyVigilance SafetyDB</span>
          <span className="text-xs bg-purple-100 text-purple-900 border border-purple-200 px-2.5 py-1 rounded-full font-extrabold">
            MedDRA {meddraInfo.version} ({meddraInfo.currentCount} terms)
          </span>
    </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowLineListing(true)}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm px-5 py-2.5 rounded-xl font-bold shadow-md"
          >
            📊 CIOMS II Line Listing
          </button>
          <div className="flex items-center gap-3 bg-white/60 border border-slate-200 px-4 py-1.5 rounded-2xl shadow-sm">
            <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-indigo-100 text-indigo-900">{user.role}</span>
            <span className="text-sm font-bold text-slate-700">{user.username}</span>
            <button type="button" onClick={() => { resetSession(); setUser(null); }} className="text-xs font-bold text-slate-400 hover:text-red-500 ml-2">Logout</button>
          </div>
        </div>
      </div>

      {msg && (
        <div className={`fixed top-20 right-6 z-50 px-6 py-4 rounded-2xl shadow-xl text-sm font-bold ${msg.type === "error" ? "bg-red-50 text-red-800 border-2 border-red-200" : "bg-green-50 text-green-800 border-2 border-green-200"}`}>
          {msg.text}
        </div>
      )}

      {/* View Switcher */}
      <div className="bg-white/60 backdrop-blur-md border-b border-slate-200 px-8 flex items-center gap-2">
        {[{ id: "dashboard", label: "📋 Workflow Dashboard" }, { id: "search", label: "🔍 Case Search" }].map(v => (
          <button
            key={v.id}
            type="button"
            onClick={() => setActiveView(v.id)}
            className={`text-sm px-5 py-4 font-extrabold border-b-2 transition-all ${activeView === v.id ? "border-indigo-600 text-indigo-800" : "border-transparent text-slate-500"}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {activeView === "search" && (
        <SearchPanel
          cases={cases}
          onOpenCase={(c) => {
            setSelected(c); setForm(c); setShowAudit(false); setTab("general"); fetchAudit(c.id);
          }}
        />
      )}

      {activeView === "dashboard" && (
        <div className="max-w-7xl mx-auto px-6 py-8 flex-1 w-full space-y-6">
          <div className="bg-white/80 backdrop-blur-md rounded-3xl border border-white p-8 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-sm font-extrabold text-slate-500 uppercase tracking-widest">Workflow Status &amp; Compliance Overview</h3>
              <button type="button" onClick={fetchCases} className="text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg">↻ Refresh</button>
            </div>
            <div style={{ height: 170 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b", fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#4f46e5" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {user.step === 1 && renderTriageForm()}

          {/* Kanban Columns */}
          <div className="grid grid-cols-6 gap-4">
            {STAGES.map(stage => {
              const stageCases = cases.filter(c => c.currentStep === stage.step);
              return (
                <div
                  key={stage.step}
                  className={`rounded-3xl p-4 border-2 min-h-32 ${stage.step === user.step ? "bg-indigo-50/70 border-indigo-300" : "bg-white/80 border-white"}`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-extrabold text-xs uppercase tracking-widest text-slate-600">{stage.name}</h4>
                    <span className="text-xs bg-indigo-100 text-indigo-800 px-2.5 py-0.5 rounded-full font-bold">{stageCases.length}</span>
                  </div>
                  {stageCases.map(c => {
                    const ind = getDBWorkflowIndicator(c);
                    const ser = getUnifiedSeriousness(c);
                    const hasNonCurr = (c.events || []).some(ev => ev.non_current);
                    return (
                      <div
                        key={c.id}
                        onClick={() => {
                          setSelected(c); setForm(c); setShowAudit(false); setTab("general"); fetchAudit(c.id);
                        }}
                        className="p-3 rounded-2xl mb-3 cursor-pointer border bg-white hover:border-indigo-400 shadow-sm transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-extrabold text-xs text-indigo-700">{c.locked ? "🔒 " : ""}{c.caseNumber}</span>
                          {ser.fLtTag !== "No" && (
                            <span className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-extrabold">{ser.fLtTag}</span>
                          )}
                        </div>
                        <div className="text-xs mt-1 truncate font-medium text-slate-500">
                          {c.triage?.country || "—"} · {(c.products || [])[0]?.name || "—"}
                        </div>
                        {hasNonCurr && (
                          <div className="text-[10px] mt-1 bg-red-50 text-red-700 px-1.5 py-0.5 rounded font-bold">
                            * Non-Current MedDRA Term
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-between text-[11px] font-bold text-slate-600">
                          <span>{ind.icon} {ind.label}</span>
                          {(c.followUps || []).length > 0 && <span className="text-purple-700">F/U #{c.followUps.length}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {stageCases.length === 0 && <div className="text-xs text-slate-400 font-bold text-center py-8">Empty</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MedDRA 29.1 Self-Service Upgrade & Recode Modal */}
      {upgradeModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-6 w-[540px] shadow-2xl">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-extrabold text-slate-800 text-lg">🧬 MedDRA 29.1 Dictionary Upgrade &amp; Self-Service Recoding</h4>
              <button type="button" onClick={() => setUpgradeModalOpen(false)} className="text-slate-400 hover:text-red-500 font-bold">✕</button>
            </div>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">
              Upload <strong>version_report_29_1_English v2.xlsx</strong> (or leave empty to run the server copy). This applies all 417 New LLTs, 175 New PTs, 19 Promotions, 51 Demotions, 224 PT moves, SOC updates, and 13 Currency changes, then auto-recodes unlocked cases to MedDRA 29.1.
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={e => setUpgradeFile(e.target.files?.[0] || null)}
              className="block w-full text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 mb-4"
            />
            {upgradeStats && (
              <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 text-xs space-y-1 mb-4">
                <div className="font-extrabold text-purple-900 mb-1">✅ MedDRA 29.1 Upgrade Summary:</div>
                <div>• New LLTs / PTs Inserted: <strong>{upgradeStats.new_llts_inserted}</strong></div>
                <div>• Promotions / Demotions Applied: <strong>{upgradeStats.promotions_applied} / {upgradeStats.demotions_applied}</strong></div>
                <div>• LLTs Moved Under Different PTs: <strong>{upgradeStats.pt_moves_applied}</strong></div>
                <div>• Primary SOC &amp; Term Name Updates: <strong>{upgradeStats.soc_changes_applied + upgradeStats.name_changes_applied}</strong></div>
                <div>• Currency Flips Applied (*): <strong>{upgradeStats.currency_changes_applied}</strong></div>
                <div>• Unlocked Cases Auto-Recoded: <strong>{upgradeStats.cases_recoded}</strong> ({upgradeStats.cases_flagged_non_current} flagged with non-current *)</div>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setUpgradeModalOpen(false)} className="px-4 py-2 text-xs font-bold text-slate-500">Close</button>
              <button
                type="button"
                disabled={upgradeLoading}
                onClick={handleRunMeddraUpgrade}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-md"
              >
                {upgradeLoading ? "⏳ Upgrading & Recoding…" : "⚡ Run MedDRA 29.1 Upgrade"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Match Score Explanation Modal */}
      {scoreModalDup && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-6 w-[560px] shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-extrabold text-slate-800">🔍 Match Score Explanation — {scoreModalDup.caseNumber}</h4>
              <button type="button" onClick={() => setScoreModalDup(null)} className="text-slate-400 hover:text-red-500 font-bold">✕</button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Total Similarity Score: <strong>{scoreModalDup.score}% ({scoreModalDup.band})</strong></p>
            <table className="w-full text-xs border-collapse mb-4">
              <thead>
                <tr className="bg-slate-100 text-left">
                  <th className="p-2">Field</th>
                  <th className="p-2">Search Criteria</th>
                  <th className="p-2">Selected Record</th>
                  <th className="p-2">Score</th>
                  <th className="p-2">Match Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(scoreModalDup.breakdown || []).map((b, i) => (
                  <tr key={i}>
                    <td className="p-2 font-bold">{b.field}</td>
                    <td className="p-2">{b.searched || "—"}</td>
                    <td className="p-2">{b.matched || "—"}</td>
                    <td className="p-2 font-mono font-bold">{b.score}</td>
                    <td className="p-2">{b.matchType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end">
              <button type="button" onClick={() => setScoreModalDup(null)} className="bg-slate-800 text-white text-xs px-4 py-2 rounded-xl font-bold">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Case Lock / Unlock Modal */}
      {lockModalOpen && selected && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-6 w-[460px] shadow-2xl">
            <h4 className="font-extrabold text-slate-800 mb-2">
              {selected.locked ? "🔓 Unlock Case Form" : "🔒 Global Case Lock"} — {selected.caseNumber}
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              {selected.locked
                ? "Unlocking allows modifications and optionally logs a new Follow-Up version."
                : "Locking freezes case data for final regulatory report generation."}
            </p>
            {selected.locked && (
              <div className="mb-3">
                {F("Unlock Reason / Follow-Up Type", S([
                  { v: "significant", l: "Significant Follow-Up — (S) F/U (updates Aware Date)" },
                  { v: "non-significant", l: "Non-Significant Follow-Up — (NS) F/U" },
                  { v: "correction", l: "Administrative Unlock (no new follow-up version)" }
                ], { value: lockForm.followUpType, onChange: e => setLockForm({ ...lockForm, followUpType: e.target.value }) }))}
              </div>
            )}
            {F("Justification Notes *", TA({ rows: 2, placeholder: "Enter lock/unlock justification…", value: lockForm.notes, onChange: e => setLockForm({ ...lockForm, notes: e.target.value }) }), true)}
            {F("Confirm Your Password *", <input type="password" className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" placeholder="Enter your login password" value={lockForm.password} onChange={e => setLockForm({ ...lockForm, password: e.target.value })} />, true)}
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setLockModalOpen(false)} className="px-4 py-2 text-xs font-bold text-slate-500">Cancel</button>
              <button type="button" onClick={submitCaseLockToggle} className="bg-indigo-600 text-white px-5 py-2 rounded-xl text-xs font-bold">
                {selected.locked ? "Confirm Unlock" : "Confirm Global Lock"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Line Listing Modal */}
      {showLineListing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex flex-col z-50">
          <div className="bg-white flex flex-col flex-1 overflow-hidden m-4 rounded-3xl shadow-2xl">
            <div className="bg-gradient-to-r from-indigo-900 to-blue-900 px-8 py-5 flex items-center justify-between">
              <div>
                <div className="text-white font-extrabold text-xl">📊 CIOMS II / ICSR Line Listing (MedDRA 29.1)</div>
                <div className="text-indigo-200 text-xs mt-1">{cases.length} total cases · Confidentiality flags respected</div>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => exportLineListing("csv")} className="bg-white/10 text-white border border-white/30 text-xs px-4 py-2 rounded-xl font-bold">⬇ Export CSV</button>
                <button type="button" onClick={() => exportLineListing("pdf")} className="bg-white/10 text-white border border-white/30 text-xs px-4 py-2 rounded-xl font-bold">📄 Export PDF</button>
                <button type="button" onClick={() => setShowLineListing(false)} className="text-white text-2xl ml-3">✕</button>
              </div>
            </div>
            <div className="bg-indigo-50/50 border-b border-indigo-100 px-8 py-3 flex items-center gap-4">
              <input placeholder="Filter case #, drug, PT, country…" className="bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm w-80" value={llFilter} onChange={e => setLlFilter(e.target.value)} />
              <select className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm" value={llSeriousFilter} onChange={e => setLlSeriousFilter(e.target.value)}>
                <option value="all">All Seriousness</option>
                <option value="serious">Serious Only</option>
                <option value="nonserious">Non-serious Only</option>
              </select>
            </div>
            <div className="overflow-auto flex-1">
              <table className="min-w-max w-full text-xs border-collapse">
                <thead className="bg-indigo-50 sticky top-0">
                  <tr className="text-left text-indigo-900">
                    <th className="p-3">Case #</th>
                    <th className="p-3">Aware Date</th>
                    <th className="p-3">Ver</th>
                    <th className="p-3">F/LT/H</th>
                    <th className="p-3">Clock</th>
                    <th className="p-3">Country</th>
                    <th className="p-3">Patient</th>
                    <th className="p-3">Drug(s)</th>
                    <th className="p-3">MedDRA 29.1 PT(s)</th>
                    <th className="p-3">Seriousness</th>
                    <th className="p-3">Causality</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lineListingRows()
                    .filter(r => !llFilter || r.caseNumber.toLowerCase().includes(llFilter.toLowerCase()) || r.drug.toLowerCase().includes(llFilter.toLowerCase()) || r.pt.toLowerCase().includes(llFilter.toLowerCase()))
                    .filter(r => llSeriousFilter === "all" ? true : llSeriousFilter === "serious" ? r.serious !== "Non-serious" : r.serious === "Non-serious")
                    .map(r => (
                      <tr
                        key={r.caseNumber}
                        className="hover:bg-indigo-50/50 cursor-pointer"
                        onClick={() => {
                          const c = cases.find(x => x.caseNumber === r.caseNumber);
                          if (c) { setSelected(c); setForm(c); fetchAudit(c.id); setShowLineListing(false); }
                        }}
                      >
                        <td className="p-3 font-mono font-bold text-indigo-700">{r.locked ? "🔒 " : ""}{r.caseNumber}</td>
                        <td className="p-3">{r.receiptDate}</td>
                        <td className="p-3 font-bold text-purple-700">{r.followUpTag}</td>
                        <td className="p-3 font-extrabold text-red-700">{r.fLtTag}</td>
                        <td className="p-3 font-bold">{r.clockLabel}</td>
                        <td className="p-3">{r.country}</td>
                        <td className="p-3 font-mono">{r.patientInitials} ({r.age})</td>
                        <td className="p-3 font-bold">{r.drug}</td>
                        <td className="p-3 text-purple-700 font-bold">{r.pt}</td>
                        <td className="p-3">{r.serious}</td>
                        <td className="p-3">{r.causality}</td>
                        <td className="p-3"><span className="bg-indigo-100 text-indigo-900 px-2 py-0.5 rounded-full font-bold">{r.status}</span></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Case Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-start pt-8 pb-8 z-40 overflow-y-auto">
          <div className="bg-white rounded-3xl w-[920px] shadow-2xl mx-4 flex flex-col max-h-[88vh]">
            <div className="px-8 py-5 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white rounded-t-3xl z-20">
              <div>
                <div className="font-extrabold text-slate-900 text-xl flex items-center gap-2">
                  <span>{selected.locked ? "🔒" : "🔓"} {selected.caseNumber}</span>
                  <span className="text-xs bg-indigo-50 text-indigo-800 px-2.5 py-1 rounded-full font-bold">
                    {getDBWorkflowIndicator(selected).icon} {getDBWorkflowIndicator(selected).label}
                  </span>
                </div>
                <div className="text-xs font-medium text-slate-500 mt-1 flex items-center gap-2">
                  <span>Step {selected.currentStep}: <strong>{selected.status}</strong></span>
                  <span>· Aware Date: {form.general?.awareDate || form.triage?.receiptDate || "—"}</span>
                  {(form.followUps || []).length > 0 && <span className="text-purple-700 font-bold">· Follow-Up #{form.followUps.length}</span>}
                </div>
              </div>

              <div className="flex gap-2 flex-wrap justify-end items-center">
                {Number(selected.currentStep) === 5 && Number(user?.step) === 5 && String(user?.role || "").toLowerCase() === "submissions" && (
                  <button
                    type="button"
                    onClick={() => setLockModalOpen(true)}
                    className={`text-xs px-3.5 py-2 rounded-xl font-bold border-2 ${selected.locked ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  >
                    {selected.locked ? "🔓 Unlock Case" : "🔒 Lock Case"}
                  </button>
                )}
                <button type="button" onClick={exportCIOMS} className="bg-white border-2 border-indigo-100 text-indigo-800 text-xs px-3.5 py-2 rounded-xl font-bold">
                  📄 CIOMS I {!(selected.locked || Number(selected.currentStep) >= 5) && "(Draft)"}
                </button>
                <button type="button" onClick={exportMedWatch} className="bg-white border-2 border-rose-100 text-rose-800 text-xs px-3.5 py-2 rounded-xl font-bold">
                  📋 MedWatch {!(selected.locked || Number(selected.currentStep) >= 5) && "(Draft)"}
                </button>
                {selected.currentStep >= 3 && (
                  <button type="button" onClick={downloadE2B} className="bg-white border-2 border-violet-100 text-violet-800 text-xs px-3.5 py-2 rounded-xl font-bold">
                    📨 E2B R3
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowAudit(a => !a)}
                  className={`text-xs px-3.5 py-2 rounded-xl font-bold border-2 ${showAudit ? "bg-amber-500 text-white border-amber-500" : "bg-white border-amber-200 text-amber-800"}`}
                >
                  📋 Audit ({auditLog.length})
                </button>
                {isMyCase(selected) && selected.currentStep === 5 && !showAudit && (
                  <button type="button" onClick={returnCaseToQuality} className="bg-orange-50 border-2 border-orange-200 text-orange-900 text-xs px-3.5 py-2 rounded-xl font-bold">
                    ↩️ Return to QC
                  </button>
                )}
                {isMyCase(selected) && selected.currentStep < 7 && !showAudit && (
                  <button type="button" onClick={updateCase} className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-5 py-2 rounded-xl font-bold shadow-md">
                    Route Next →
                  </button>
                )}
                <button type="button" onClick={() => { setSelected(null); setShowAudit(false); }} className="bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-600 text-lg font-bold w-9 h-9 rounded-xl flex items-center justify-center">✕</button>
              </div>
            </div>

            <div className="p-8 overflow-y-auto flex-1 bg-slate-50/30">
              {showAudit ? (
                <div>
                  <div className="text-xs font-extrabold text-amber-700 uppercase tracking-widest mb-4 border-b border-amber-100 pb-2">📋 Audit Log &amp; Revision History — {selected.caseNumber}</div>
                  {auditLog.map((entry, i) => {
                    const meta = ACTION_META[entry.actionType] || { color: "bg-slate-100 text-slate-700", icon: "•", label: entry.actionType };
                    return (
                      <div key={i} className="flex gap-4 mb-4 pb-4 border-b border-slate-100">
                        <div className="text-xl">{meta.icon}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                            <span className="text-xs text-slate-400">{entry.timestamp}</span>
                          </div>
                          <div className="text-xs text-slate-600 font-bold">{entry.performedBy} ({entry.role})</div>
                          {entry.details && <div className="mt-1 text-xs text-slate-600 bg-white border border-slate-100 rounded-lg p-2">{entry.details}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : renderModalForm()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
